import {
    authService,
    supabase,
    WorkspaceIntegrationAPI,
    type ConnectionScene
} from '@common/api/authService';
import { getCurrentProductCode, isPluginProduct, withPluginProductHeaders } from '@common/api/productContext';
import Nango, { type AuthOptions } from '@nangohq/frontend';
import type { AxiosRequestConfig } from 'axios';
import {
    buildWaitingPageHtml,
    getBrowserWaitingPageContent,
} from '@/components/WaitingPage';
import {
    dispatchBustlyHostedConnect,
} from '@/components/Settings/components/integrations/bustlyHostedConnectProviders';
import { resolveShopifyDisconnectDomain } from '@/components/Onboarding/storeUrlNormalization';
import {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { preserveGoogleAdsExternalAccountNames } from './integrationDisplayHydration';
import {
    requiresConnectionScopedDisconnect,
    resolveFigmaDisconnectTransport,
} from './integrationDisconnectRouting';
import {
    persistBustlyHostedConnectHandoff,
    waitForBustlyHostedConnectPopupResult,
} from './bustlyHostedConnect';
import {
    advanceWorkspaceIntegrationFetchScope,
    createWorkspaceIntegrationFetchScope,
    hasFreshWorkspaceIntegrationSnapshot,
    invalidateWorkspaceIntegrationFetchScope,
    isSameWorkspaceIntegrationFetchScope,
    type WorkspaceIntegrationFetchScope,
} from './workspaceIntegrationFreshness';

// 集成状态枚举
export type IntegrationStatus = 'ACTIVE' | 'INACTIVE' | 'ERROR';

// 扩展 API 类型，添加 connected_at 字段
export interface WorkspaceIntegration extends WorkspaceIntegrationAPI {
}

interface WorkspaceIntegrationsFetchResult {
    integrations: WorkspaceIntegration[];
    error: Error | null;
}

interface WorkspaceIntegrationsInFlightEntry {
    promise: Promise<WorkspaceIntegrationsFetchResult>;
    superseded: boolean;
}

const workspaceIntegrationsCache = new Map<string, WorkspaceIntegration[]>();
const workspaceIntegrationsInFlight = new Map<string, WorkspaceIntegrationsInFlightEntry>();
const workspaceIntegrationsFetchedAt = new Map<string, number>();
const WORKSPACE_INTEGRATIONS_SILENT_REFRESH_DEDUPE_MS = 5000;
const readCachedWorkspaceIntegrations = (workspaceId?: string): WorkspaceIntegration[] => (
    workspaceId ? (workspaceIntegrationsCache.get(workspaceId) || []) : []
);

const normalizeWorkspaceIntegrationsError = (error: unknown): Error => (
    error instanceof Error
        ? error
        : new Error('Failed to load workspace integrations')
);

type WorkspaceIntegrationTimingKey =
    | 'workspaceIntegrationsApi';

type WorkspaceIntegrationTimingTracker = {
    record: (key: WorkspaceIntegrationTimingKey, elapsedMs: number) => void;
    toLogPayload: () => Record<string, unknown>;
};

const nowForTiming = () => (
    typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
);

const createWorkspaceIntegrationTimingTracker = (workspaceId?: string): WorkspaceIntegrationTimingTracker => {
    const startedAt = nowForTiming();
    const timings: Partial<Record<WorkspaceIntegrationTimingKey, number>> = {};

    return {
        record: (key, elapsedMs) => {
            timings[key] = Math.round(elapsedMs);
        },
        toLogPayload: () => ({
            workspaceId,
            totalMs: Math.round(nowForTiming() - startedAt),
            ...timings,
        }),
    };
};

const trackPromiseTiming = async <T,>(
    timingTracker: WorkspaceIntegrationTimingTracker,
    key: WorkspaceIntegrationTimingKey,
    promise: PromiseLike<T>
): Promise<T> => {
    const startedAt = nowForTiming();
    try {
        return await promise;
    } finally {
        timingTracker.record(key, nowForTiming() - startedAt);
    }
};

// 按平台分组的集成状态 - 直接使用 API 数据结构
export interface WorkspaceIntegrationsState {
    shopify: WorkspaceIntegration | null;
    bigCommerce: WorkspaceIntegration | null;
    googleAds: WorkspaceIntegration | null;
    googleAnalytics: WorkspaceIntegration | null;
    tiktokAds: WorkspaceIntegration | null;
    googleSearchConsole: WorkspaceIntegration | null;
    semrush: WorkspaceIntegration | null;
    slack: WorkspaceIntegration | null;
    woocommerce: WorkspaceIntegration | null;
    magento: WorkspaceIntegration | null;
    prestashop: WorkspaceIntegration | null;
    aliexpress: WorkspaceIntegration | null;
    klaviyo: WorkspaceIntegration | null;
    mailchimp: WorkspaceIntegration | null;
    wix: WorkspaceIntegration | null;
    metaAds: WorkspaceIntegration | null;
    facebookPage: WorkspaceIntegration | null;
    instagram: WorkspaceIntegration | null;
    similarweb: WorkspaceIntegration | null;
    ahrefs: WorkspaceIntegration | null;
    omnisend: WorkspaceIntegration | null;
    okendo: WorkspaceIntegration | null;
    goaffpro: WorkspaceIntegration | null;
    uppromote: WorkspaceIntegration | null;
    loox: WorkspaceIntegration | null;
    trustwill: WorkspaceIntegration | null;
    noxinfluencer: WorkspaceIntegration | null;
    insiderOne: WorkspaceIntegration | null;
    emarsys: WorkspaceIntegration | null;
    judgeme: WorkspaceIntegration | null;
    linkedinAds: WorkspaceIntegration | null;
    pinterestAds: WorkspaceIntegration | null;
    xAds: WorkspaceIntegration | null;
    youtube: WorkspaceIntegration | null;
    reddit: WorkspaceIntegration | null;
    attribuly: WorkspaceIntegration | null;
    snapchat: WorkspaceIntegration | null;
    feishu: WorkspaceIntegration | null;
    dingtalk: WorkspaceIntegration | null;
    figma: WorkspaceIntegration | null;
}

export interface DisconnectIntegrationOptions {
    connectionId?: string;
    workspaceIntegrationId?: string;
    shopDomain?: string;
    authType?: string;
    native?: boolean;
    connectionBackend?: 'NANGO' | 'BUSTLY' | 'NATIVE' | 'ZERNIO';
    connectionScene?: ConnectionScene;
}

interface FetchWorkspaceIntegrationsOptions {
    forceRefresh?: boolean;
}

// Hook 返回类型
export interface UseWorkspaceIntegrationsReturn {
    integrations: WorkspaceIntegrationsState;
    allIntegrations: WorkspaceIntegration[];
    isLoading: boolean;
    hasFetchedCurrentWorkspace: boolean;
    error: Error | null;
    refresh: (showLoading?: boolean, options?: FetchWorkspaceIntegrationsOptions) => Promise<WorkspaceIntegration[]>;
    isConnected: (platform: string) => boolean;
    getIntegration: (platform: string) => WorkspaceIntegration | null;
    connect: (
        platform: string,
        connectionConfig?: any,
        options?: {
            onAuthorizationStarted?: () => void | Promise<void>;
            onSuccess?: () => void | Promise<void>;
            onError?: (error: string) => void | Promise<void>;
            onRestriction?: (payload: ConnectRestrictionPayload) => void | Promise<void>;
        }
    ) => Promise<any>;
    disconnect: (platform: string, options?: DisconnectIntegrationOptions) => Promise<void>;
    hasConnectedIntegrations: boolean;
}

interface UseWorkspaceIntegrationsOptions {
    enabled?: boolean;
    requireFreshFetch?: boolean;
}

type ConnectRestrictionPayload = {
    restrictionState?: WorkspaceIntegrationAPI['restrictionState'];
    eligibilityState?: WorkspaceIntegrationAPI['eligibilityState'];
    reasonCode?: string | null;
    blockConnect?: boolean;
    upgradeRequired?: boolean;
    ctaAction?: string | null;
    allowsFreshTrial?: boolean;
    message?: string | null;
    eligibilityMessage?: string | null;
    trialRemainingDays?: number | null;
    currentWorkspaceId?: string | null;
    currentWorkspaceName?: string | null;
    currentStoreCount?: number | null;
    storeLimit?: number | null;
    upgradeTarget?: 'TEAM' | 'SALES' | null;
};

// API 返回的 provider 名称到内部 key 的映射
const PROVIDER_TO_KEY: Record<string, keyof WorkspaceIntegrationsState> = {
    'shopify': 'shopify',
    'google-ads': 'googleAds',
    'google-analytics': 'googleAnalytics',
    'tiktok-ads': 'tiktokAds',
    'google-search-console': 'googleSearchConsole',
    'semrush': 'semrush',
    'slack': 'slack',
    'bigcommerce': 'bigCommerce',
    'woocommerce': 'woocommerce',
    'magento': 'magento',
    'adobe-commerce': 'magento',  // Nango 返回的 provider
    'prestashop': 'prestashop',
    'presta-shop': 'prestashop',
    'aliexpress': 'aliexpress',
    'klaviyo': 'klaviyo',
    'klaviyo-oauth': 'klaviyo',  // Nango 返回的 provider
    'mailchimp': 'mailchimp',
    'mailchimp-oauth': 'mailchimp',
    'wix': 'wix',
    'meta-ads': 'metaAds',
    'metaads': 'metaAds',
    'facebook-page': 'facebookPage',
    'facebook': 'facebookPage',
    'instagram': 'instagram',
    'similarweb': 'similarweb',
    'ahrefs': 'ahrefs',
    'omnisend': 'omnisend',
    'okendo': 'okendo',
    'goaffpro': 'goaffpro',
    'go-aff-pro': 'goaffpro',
    'uppromote': 'uppromote',
    'up-promote': 'uppromote',
    'loox': 'loox',
    'loox-reviews': 'loox',
    'trustwill': 'trustwill',
    'trustwill-loyalty': 'trustwill',
    'loloyal': 'trustwill',
    'lo-loyal': 'trustwill',
    'noxinfluencer': 'noxinfluencer',
    'nox-influencer': 'noxinfluencer',
    'nox': 'noxinfluencer',
    'insider-one': 'insiderOne',
    'insider-one-oauth': 'insiderOne',
    'insider': 'insiderOne',
    'useinsider': 'insiderOne',
    'emarsys': 'emarsys',
    'emarsys-oauth': 'emarsys',
    'sap-emarsys': 'emarsys',
    'sap-engagement-cloud': 'emarsys',
    'judgeme': 'judgeme',
    'judgeme-oauth': 'judgeme',
    'judge-me': 'judgeme',
    'judge.me': 'judgeme',
    'judge': 'judgeme',
    'linkedin-ads': 'linkedinAds',
    'linkedinads': 'linkedinAds',
    'linkedin': 'linkedinAds',
    'pinterest-ads': 'pinterestAds',
    'pinterestads': 'pinterestAds',
    'pinterest': 'pinterestAds',
    'x-ads': 'xAds',
    'xads': 'xAds',
    'twitter-ads': 'xAds',
    'twitterads': 'xAds',
    'twitter-x': 'xAds',
    'twitterx': 'xAds',
    'twitter': 'xAds',
    'x': 'xAds',
    'youtube-ads': 'youtube',
    'youtubeads': 'youtube',
    'youtube': 'youtube',
    'reddit': 'reddit',
    'reddit-social': 'reddit',
    'attribuly': 'attribuly',
    'attribuly-attribution': 'attribuly',
    'snapchat-ads': 'snapchat',
    'snapchatads': 'snapchat',
    'snapchat': 'snapchat',
    'feishu': 'feishu',
    'dingtalk': 'dingtalk',
    'ding-talk': 'dingtalk',
    'dingding': 'dingtalk',
    'figma': 'figma',
    'figma-oauth': 'figma',
};

const JUDGEME_OAUTH_PROVIDERS = new Set(['judgeme', 'judgeme-oauth', 'judge-me', 'judge.me', 'judge']);

// 平台到 Nango integration ID 的映射
const PLATFORM_TO_NANGO_ID: Record<string, string> = {
    'shopify': 'shopify',
    'google-ads': 'google-ads',
    'google-analytics': 'google-analytics',
    'tiktok-ads': 'tiktok-ads',
    'google-search-console': 'google-search-console',
    'semrush': 'semrush',
    'slack': 'slack',
    'bigcommerce': 'bigcommerce',
    'woocommerce': 'woocommerce',
    'magento': 'adobe-commerce',
    'adobe-commerce': 'adobe-commerce',
    'aliexpress': 'aliexpress',
    'klaviyo': 'klaviyo-oauth',
    'klaviyo-oauth': 'klaviyo-oauth',
    'mailchimp': 'mailchimp',
    'similarweb': 'similarweb',
    'ahrefs': 'ahrefs',
    'omnisend': 'omnisend',
    'okendo': 'okendo',
    'goaffpro': 'goaffpro',
    'uppromote': 'uppromote',
    'loox': 'loox',
    'trustwill': 'trustwill',
    'noxinfluencer': 'noxinfluencer',
    'insider-one': 'insider-one-oauth',
    'insider-one-oauth': 'insider-one-oauth',
    'insider-one-api-key': 'insider-one',
    'emarsys': 'emarsys-oauth',
    'emarsys-oauth': 'emarsys-oauth',
    'mailchimp-oauth': 'mailchimp',
    'figma': 'figma',
    'figma-oauth': 'figma',
};

const MANUAL_API_KEY_PROVIDER: Record<string, string> = {
    'attribuly': 'attribuly',
    'attribuly-attribution': 'attribuly',
    'insider-one-api-key': 'insider-one',
    'presta-shop': 'prestashop',
    'go-aff-pro': 'goaffpro',
    'up-promote': 'uppromote',
    'loox-reviews': 'loox',
    'trustwill-loyalty': 'trustwill',
    'loloyal': 'trustwill',
    'lo-loyal': 'trustwill',
    'nox-influencer': 'noxinfluencer',
    'nox': 'noxinfluencer',
};

const ZERNIO_PROVIDERS = new Set([
    'meta-ads',
    'metaads',
    'facebook-page',
    'facebook',
    'instagram',
    'linkedin-ads',
    'linkedinads',
    'linkedin',
    'pinterest-ads',
    'pinterestads',
    'pinterest',
    'x-ads',
    'xads',
    'twitter-ads',
    'twitterads',
    'twitter-x',
    'twitterx',
    'twitter',
    'x',
    'youtube-ads',
    'youtubeads',
    'youtube',
    'reddit',
    'reddit-social',
    'snapchat-ads',
    'snapchatads',
    'snapchat',
]);

const repeatedBindingMessage = 'This store is already bound to another workspace.';
const applicationTrialRemainingMessage = 'This application account already has remaining trial time.';
const applicationRepeatedBindingMessage = 'This application account is already bound to another workspace.';

const inferConnectionLimitRestrictionFromMessage = (message: string): ConnectRestrictionPayload | null => {
    const normalizedMessage = message.trim();
    const otherIntegrationLimit = normalizedMessage.match(
        /supports up to\s+(\d+)\s+(?:other|non-store)\s+(?:app|integration)\s+connections?/i,
    );
    if (otherIntegrationLimit) {
        return {
            reasonCode: 'integration_group_limit_reached',
            restrictionState: 'integration_group_limit_reached',
            eligibilityState: 'integration_group_limit_reached',
            blockConnect: true,
            upgradeRequired: true,
            message: normalizedMessage,
            eligibilityMessage: normalizedMessage,
        };
    }

    const totalIntegrationLimit = normalizedMessage.match(
        /supports up to\s+(\d+)\s+total\s+(?:app|integration)\s+connections?/i,
    );
    if (totalIntegrationLimit) {
        return {
            reasonCode: 'total_connection_limit_reached',
            restrictionState: 'total_connection_limit_reached',
            eligibilityState: 'total_connection_limit_reached',
            blockConnect: true,
            upgradeRequired: true,
            message: normalizedMessage,
            eligibilityMessage: normalizedMessage,
        };
    }

    return null;
};

const isConnectRestrictionPayload = (value: unknown): value is ConnectRestrictionPayload => {
    return Boolean(
        value
        && typeof value === 'object'
        && (
            'blockConnect' in value
            || 'restrictionState' in value
            || 'eligibilityState' in value
            || 'reasonCode' in value
            || 'upgradeRequired' in value
            || 'ctaAction' in value
            || 'currentWorkspaceName' in value
            || 'currentStoreCount' in value
            || 'storeLimit' in value
        )
    );
};

const readConnectRestrictionFromErrorResponse = (error: unknown): ConnectRestrictionPayload | null => {
    if (!error || typeof error !== 'object') {
        return null;
    }

    const response = (error as { response?: unknown }).response;
    const candidates: unknown[] = [response];

    const pushEnvelopeCandidates = (value: unknown) => {
        if (!value || typeof value !== 'object') {
            return;
        }
        if ('data' in value) {
            candidates.push((value as { data?: unknown }).data);
        }
        if ('extra' in value) {
            candidates.push((value as { extra?: unknown }).extra);
        }
        if ('error' in value) {
            candidates.push((value as { error?: unknown }).error);
        }
    };

    pushEnvelopeCandidates(response);
    if (response && typeof response === 'object' && 'data' in response) {
        pushEnvelopeCandidates((response as { data?: unknown }).data);
    }

    return candidates.find(isConnectRestrictionPayload) || null;
};

const readErrorResponseMessage = (error: unknown): string | null => {
    if (!error || typeof error !== 'object') {
        return null;
    }

    const responseData = (error as {
        response?: {
            data?: unknown;
        };
    }).response?.data;

    if (!responseData) {
        return null;
    }

    if (typeof responseData === 'string') {
        return responseData;
    }

    if (typeof responseData !== 'object') {
        return null;
    }

    const data = responseData as {
        message?: unknown;
        error?: unknown;
        data?: {
            message?: unknown;
            error?: unknown;
        };
    };
    const message = data.message ?? data.error ?? data.data?.message ?? data.data?.error;
    return typeof message === 'string' && message.trim() ? message : null;
};

const getErrorMessage = (error: unknown): string | null => {
    if (typeof error === 'string') return error;
    const responseMessage = readErrorResponseMessage(error);
    if (responseMessage) return responseMessage;
    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message?: unknown }).message || '');
    }
    return null;
};

const resolveRestrictionFromConnectError = (error: unknown): ConnectRestrictionPayload | null => {
    if (isConnectRestrictionPayload(error)) {
        return error;
    }

    const responseRestriction = readConnectRestrictionFromErrorResponse(error);
    if (responseRestriction) {
        return responseRestriction;
    }

    const message = getErrorMessage(error);
    if (!message) {
        return null;
    }

    try {
        const parsed = JSON.parse(message);
        if (isConnectRestrictionPayload(parsed)) {
            return parsed;
        }
    } catch {
        // Ignore non-JSON error messages.
    }

    if (
        /subscription.*(?:expired|ended)/i.test(message)
        || /订阅.*(?:过期|到期|已结束)/.test(message)
    ) {
        return {
            reasonCode: 'plan_expired',
            restrictionState: 'plan_expired',
            eligibilityState: 'plan_expired',
            blockConnect: true,
            upgradeRequired: true,
            ctaAction: 'renew_subscription',
            message,
            eligibilityMessage: message,
        };
    }

    if (
        /trial.*(?:expired|ended)/i.test(message)
        || /(?:free trial|trial).*renew/i.test(message)
        || /试用.*(?:过期|到期|已结束)/.test(message)
    ) {
        return {
            reasonCode: 'trial_expired',
            restrictionState: 'trial_expired',
            eligibilityState: 'trial_expired',
            blockConnect: true,
            upgradeRequired: true,
            ctaAction: 'renew_subscription',
            message,
            eligibilityMessage: message,
        };
    }

    if (
        /payment method.*(?:attention|required|update)/i.test(message)
        || /payment.*past due/i.test(message)
        || /支付.*(?:失败|逾期|处理|更新)/.test(message)
    ) {
        return {
            reasonCode: 'payment_past_due',
            restrictionState: 'payment_past_due',
            eligibilityState: 'payment_past_due',
            blockConnect: true,
            upgradeRequired: true,
            ctaAction: 'update_payment_method',
            message,
            eligibilityMessage: message,
        };
    }

    if (
        /application account already has remaining trial time/i.test(message)
        || /already has remaining trial time/i.test(message)
        || /应用.*(?:仍有|还有|剩余).*试用/.test(message)
    ) {
        return {
            restrictionState: 'trial_valid',
            blockConnect: true,
            upgradeRequired: true,
            allowsFreshTrial: false,
            message: applicationTrialRemainingMessage,
            eligibilityMessage: applicationTrialRemainingMessage,
        };
    }

    if (
        /(?:application|app).*already .*(?:connected|bound|occupied|used).*workspace/i.test(message)
        || /(?:application|app).*(?:connected|bound|occupied|used).*another workspace/i.test(message)
        || /应用.*(?:其他|其它|别的).*Workspace.*(?:占用|绑定|连接)/i.test(message)
    ) {
        return {
            restrictionState: 'already_bound',
            blockConnect: true,
            upgradeRequired: false,
            allowsFreshTrial: false,
            message: applicationRepeatedBindingMessage,
            eligibilityMessage: applicationRepeatedBindingMessage,
        };
    }

    if (
        /already (?:connected|bound) to another workspace/i.test(message)
        || /switch to that workspace or disconnect it there first/i.test(message)
    ) {
        return {
            restrictionState: 'already_bound',
            blockConnect: true,
            upgradeRequired: false,
            allowsFreshTrial: false,
            message: repeatedBindingMessage,
            eligibilityMessage: repeatedBindingMessage,
        };
    }

    return inferConnectionLimitRestrictionFromMessage(message);
};

const isProviderConnected = (
    integrations: WorkspaceIntegration[],
    provider: string
): boolean => {
    const targetKey = PROVIDER_TO_KEY[provider] || provider;
    return integrations.some(
        (integration) => integration.connected
            && ((PROVIDER_TO_KEY[integration.provider] || integration.provider) === targetKey)
    );
};

interface ProviderConnectionBaseline {
    connectionCount: number;
    connectionKeys: Set<string>;
}

interface IntegrationVerificationOptions {
    expectedAccount?: string;
    baseline?: ProviderConnectionBaseline;
    requireNewConnection?: boolean;
}

const normalizeProviderConnectionKey = (provider: string): string => {
    const normalizedProvider = provider.toLowerCase().replace(/[\s_]+/g, '-');
    const providerKey = normalizedProvider.startsWith('figma-') ? 'figma' : normalizedProvider;
    return String(PROVIDER_TO_KEY[providerKey] || providerKey);
};

const isMappingAvailable = (mapping: WorkspaceIntegration['mappings'][number]): boolean => {
    const status = String(
        mapping.status
        || mapping.mapping_status
        || mapping.account_status
        || ''
    ).toUpperCase();
    return !['INACTIVE', 'DISCONNECTED', 'FAILED', 'ERROR'].includes(status);
};

const normalizeOpaqueConnectionIdentity = (value: unknown): string => (
    String(value || '').trim().toLowerCase()
);

const normalizeAccountConnectionIdentity = (value: unknown): string => (
    normalizeOpaqueConnectionIdentity(value)
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
);

const getMappingConnectionIdentityValues = (
    mapping: WorkspaceIntegration['mappings'][number]
): unknown[] => [
    mapping.id,
    mapping.slugId,
    mapping.slug_id,
    mapping.connection_id,
    mapping.connectionId,
    mapping.external_connection_id,
    mapping.externalConnectionId,
    mapping.nango_connection_id,
    mapping.nangoConnectionId,
];

const getMappingAccountIdentityValues = (
    mapping: WorkspaceIntegration['mappings'][number]
): unknown[] => [
    mapping.account_name,
    mapping.account_id,
    mapping.connection_id,
    mapping.connectionId,
    mapping.store_name,
    mapping.shop_name,
    mapping.shop_domain,
    mapping.shopDomain,
    mapping.store_url,
    mapping.storeUrl,
    mapping.authorized_account_name,
    mapping.authorizedAccountName,
    mapping.authorized_account_email,
    mapping.authorizedAccountEmail,
    mapping.authorized_account_id,
    mapping.authorizedAccountId,
    mapping.external_account_name,
    mapping.externalAccountName,
    mapping.external_account_email,
    mapping.externalAccountEmail,
    mapping.external_account_id,
    mapping.externalAccountId,
];

const findConnectionMappingForLabel = (
    mappings: WorkspaceIntegration['mappings'],
    match: { connectionId?: string; shopDomain?: string } = {}
): WorkspaceIntegration['mappings'][number] | undefined => {
    const targetConnectionId = normalizeOpaqueConnectionIdentity(match.connectionId);
    const targetShopDomain = normalizeAccountConnectionIdentity(match.shopDomain);
    const hasExplicitTarget = Boolean(targetConnectionId || targetShopDomain);
    const matchedMapping = mappings.find((mapping) => {
        const matchesConnectionId = Boolean(targetConnectionId)
            && getMappingConnectionIdentityValues(mapping).some(
                (value) => normalizeOpaqueConnectionIdentity(value) === targetConnectionId
            );
        const matchesShopDomain = Boolean(targetShopDomain)
            && getMappingAccountIdentityValues(mapping).some(
                (value) => normalizeAccountConnectionIdentity(value) === targetShopDomain
            );
        return matchesConnectionId || matchesShopDomain;
    });

    if (matchedMapping || hasExplicitTarget) {
        return matchedMapping;
    }
    return mappings[0];
};

const findProviderConnectionMappingForLabel = (
    integrations: WorkspaceIntegration[],
    provider: string,
    match: { connectionId?: string; shopDomain?: string; workspaceIntegrationId?: string } = {}
): WorkspaceIntegration['mappings'][number] | undefined => {
    const targetKey = normalizeProviderConnectionKey(provider);
    const providerMappings = integrations
        .filter((integration) => (
            integration.connected
            && normalizeProviderConnectionKey(integration.provider) === targetKey
        ))
        .flatMap((integration) => integration.mappings || []);
    const targetMappingId = normalizeOpaqueConnectionIdentity(match.workspaceIntegrationId);
    if (targetMappingId) {
        const exactMapping = providerMappings.find(
            (mapping) => normalizeOpaqueConnectionIdentity(mapping.id) === targetMappingId
        );
        if (exactMapping) {
            return exactMapping;
        }
    }
    return findConnectionMappingForLabel(providerMappings, match);
};

const getMappingConnectionKey = (
    mapping: WorkspaceIntegration['mappings'][number],
    fallbackIndex: number
): string => {
    const candidates: Array<[string, unknown]> = [
        ['mapping', mapping.id],
        ['bustly', mapping.external_connection_id],
        ['bustly', mapping.externalConnectionId],
        ['nango', mapping.nango_connection_id],
        ['nango', mapping.nangoConnectionId],
        ['slug', mapping.slugId],
        ['slug', mapping.slug_id],
        ['connection', mapping.connection_id],
        ['connection', mapping.connectionId],
        ['authorized-account', mapping.authorized_account_id],
        ['authorized-email', mapping.authorized_account_email],
        ['external-account', mapping.external_account_id],
        ['external-email', mapping.external_account_email],
        ['account', mapping.account_id],
        ['store', mapping.store_name],
        ['shop', mapping.shop_name],
        ['shop-domain', mapping.shop_domain || mapping.shopDomain],
        ['store-url', mapping.store_url || mapping.storeUrl],
        ['account-name', mapping.account_name],
        ['created', mapping.created_at],
    ];
    const match = candidates.find(([, value]) => String(value || '').trim());
    return match
        ? `${match[0]}:${String(match[1]).trim().toLowerCase()}`
        : `anonymous-mapping:${fallbackIndex}`;
};

const getIntegrationConnectionKey = (
    integration: WorkspaceIntegration,
    fallbackIndex: number
): string => {
    const profileData = providerConfigObject(integration.profile_data);
    const candidates: Array<[string, unknown]> = [
        ['external-account', integration.external_account_id],
        ['external-name', integration.external_account_name],
        ['profile-account', profileData.account_id || profileData.accountId],
        ['profile-store', profileData.store_url || profileData.storeUrl || profileData.shop_domain],
        ['created', integration.created_at],
    ];
    const match = candidates.find(([, value]) => String(value || '').trim());
    return match
        ? `${match[0]}:${String(match[1]).trim().toLowerCase()}`
        : `anonymous-integration:${fallbackIndex}`;
};

const createProviderConnectionBaseline = (
    integrations: WorkspaceIntegration[],
    provider: string
): ProviderConnectionBaseline => {
    const targetKey = normalizeProviderConnectionKey(provider);
    const connectionKeys: string[] = [];

    integrations.forEach((integration, integrationIndex) => {
        if (!integration.connected || normalizeProviderConnectionKey(integration.provider) !== targetKey) {
            return;
        }

        const availableMappings = (integration.mappings || []).filter(isMappingAvailable);
        if (availableMappings.length === 0) {
            connectionKeys.push(getIntegrationConnectionKey(integration, integrationIndex));
            return;
        }

        availableMappings.forEach((mapping, mappingIndex) => {
            connectionKeys.push(getMappingConnectionKey(
                mapping,
                (integrationIndex * 10000) + mappingIndex
            ));
        });
    });

    return {
        connectionCount: connectionKeys.length,
        connectionKeys: new Set(connectionKeys),
    };
};

const hasNewProviderConnection = (
    integrations: WorkspaceIntegration[],
    provider: string,
    baseline: ProviderConnectionBaseline
): boolean => {
    const current = createProviderConnectionBaseline(integrations, provider);
    return current.connectionCount > baseline.connectionCount
        || Array.from(current.connectionKeys).some((key) => !baseline.connectionKeys.has(key));
};

const getPreferredRecoveredConnectionId = (
    mapping: WorkspaceIntegration['mappings'][number]
): string | undefined => {
    const candidates = [
        mapping.nango_connection_id,
        mapping.nangoConnectionId,
        mapping.connection_id,
        mapping.connectionId,
    ];
    const matched = candidates.find((value) => String(value || '').trim());
    return matched ? String(matched).trim() : undefined;
};

const resolveNewProviderConnectionId = (
    integrations: WorkspaceIntegration[],
    provider: string,
    baseline: ProviderConnectionBaseline
): string | undefined => {
    const targetKey = normalizeProviderConnectionKey(provider);

    for (const [integrationIndex, integration] of integrations.entries()) {
        if (!integration.connected || normalizeProviderConnectionKey(integration.provider) !== targetKey) {
            continue;
        }

        const availableMappings = (integration.mappings || []).filter(isMappingAvailable);
        for (const [mappingIndex, mapping] of availableMappings.entries()) {
            const currentKey = getMappingConnectionKey(
                mapping,
                (integrationIndex * 10000) + mappingIndex
            );
            if (!baseline.connectionKeys.has(currentKey)) {
                return getPreferredRecoveredConnectionId(mapping);
            }
        }

        if (availableMappings.length === 0) {
            const currentKey = getIntegrationConnectionKey(integration, integrationIndex);
            if (!baseline.connectionKeys.has(currentKey)) {
                const profileData = providerConfigObject(integration.profile_data);
                const candidates = [
                    profileData.nango_connection_id,
                    profileData.nangoConnectionId,
                    profileData.connection_id,
                    profileData.connectionId,
                ];
                const matched = candidates.find((value) => String(value || '').trim());
                return matched ? String(matched).trim() : undefined;
            }
        }
    }

    return undefined;
};

const isProviderConnectionConnected = (
    integrations: WorkspaceIntegration[],
    provider: string,
    connectionId?: string,
    expectedAccount?: string
): boolean => {
    const targetKey = PROVIDER_TO_KEY[provider] || provider;
    const expectedConnectionId = normalizeOpaqueConnectionIdentity(connectionId);
    const expectedAccountId = normalizeAccountConnectionIdentity(expectedAccount);
    return integrations.some((integration) => {
        const integrationKey = PROVIDER_TO_KEY[integration.provider] || integration.provider;
        if (!integration.connected || integrationKey !== targetKey) {
            return false;
        }
        if (!expectedConnectionId) {
            if (!expectedAccountId) {
                return true;
            }
            return (integration.mappings || []).some((mapping) => (
                getMappingAccountIdentityValues(mapping).some(
                    (value) => normalizeAccountConnectionIdentity(value) === expectedAccountId
                )
            ));
        }
        return (integration.mappings || []).some((mapping) => (
            getMappingConnectionIdentityValues(mapping).some(
                (value) => normalizeOpaqueConnectionIdentity(value) === expectedConnectionId
            )
            && (!expectedAccountId || getMappingAccountIdentityValues(mapping).some(
                (value) => normalizeAccountConnectionIdentity(value) === expectedAccountId
            ))
        ));
    });
};

const isRequestedProviderConnectionNew = (
    integrations: WorkspaceIntegration[],
    provider: string,
    connectionId: string | undefined,
    expectedAccount: string | undefined,
    baseline: ProviderConnectionBaseline
): boolean => {
    const targetKey = normalizeProviderConnectionKey(provider);
    const expectedConnectionId = normalizeOpaqueConnectionIdentity(connectionId);
    const expectedAccountId = normalizeAccountConnectionIdentity(expectedAccount);

    return integrations.some((integration, integrationIndex) => {
        if (!integration.connected || normalizeProviderConnectionKey(integration.provider) !== targetKey) {
            return false;
        }
        return (integration.mappings || []).some((mapping: any, mappingIndex) => {
            if (!isMappingAvailable(mapping)) {
                return false;
            }
            const matchesConnectionId = !expectedConnectionId
                || getMappingConnectionIdentityValues(mapping).some(
                    (value) => normalizeOpaqueConnectionIdentity(value) === expectedConnectionId
                );
            const matchesExpectedAccount = !expectedAccountId
                || getMappingAccountIdentityValues(mapping).some(
                    (value) => normalizeAccountConnectionIdentity(value) === expectedAccountId
                );
            if (!matchesConnectionId || !matchesExpectedAccount) {
                return false;
            }
            const currentKey = getMappingConnectionKey(
                mapping,
                (integrationIndex * 10000) + mappingIndex
            );
            return !baseline.connectionKeys.has(currentKey);
        });
    });
};

const isIntegrationVerificationSatisfied = (
    integrations: WorkspaceIntegration[],
    provider: string,
    connectionId?: string,
    options: IntegrationVerificationOptions = {}
): boolean => {
    const hasRequestedIdentity = Boolean(connectionId || options.expectedAccount);
    if (hasRequestedIdentity && options.requireNewConnection && options.baseline) {
        return isRequestedProviderConnectionNew(
            integrations,
            provider,
            connectionId,
            options.expectedAccount,
            options.baseline
        );
    }
    const matchesRequestedConnection = hasRequestedIdentity
        ? isProviderConnectionConnected(integrations, provider, connectionId, options.expectedAccount)
        : isProviderConnected(integrations, provider);
    if (!matchesRequestedConnection) {
        return false;
    }
    if (options.requireNewConnection && options.baseline) {
        return hasNewProviderConnection(integrations, provider, options.baseline);
    }
    return true;
};

const providerConfigObject = (value: unknown): Record<string, any> => {
    if (!value) {
        return {};
    }
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof value === 'object' ? value as Record<string, any> : {};
};

// Google Ads 账户数据类型
export interface GoogleAdsAccount {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    is_manager: string;
}

export interface GoogleAdsCustomerGroup {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    accounts: GoogleAdsAccount[];
}

type SlackOAuthPopupResult = {
    code: string;
    state: string;
};

type NativeOAuthPopupResult = {
    code?: string;
    state?: string;
    flow?: string | null;
    connected?: boolean;
    success?: boolean;
    platform?: string;
    connection?: string | null;
    integration_id?: string | number | null;
    account_name?: string | null;
    error?: string | null;
};

type ZernioOAuthPopupResult = {
    type: 'SUCCESS' | 'ERROR';
    flowId: string;
    platform: string;
    message?: string;
};

type FeishuOAuthPopupResult = {
    type: 'SUCCESS' | 'ERROR';
    provider: 'feishu';
    source?: string;
    message?: string;
    connection?: string | null;
    integration_id?: number | string | null;
    tenant_key?: string | null;
    tenant_name?: string | null;
};

type DingTalkOAuthPopupResult = {
    type: 'SUCCESS' | 'ERROR';
    provider: 'dingtalk';
    source?: string;
    message?: string;
    connection?: string | null;
    integration_id?: number | string | null;
    corp_id?: string | null;
    corp_name?: string | null;
};

const AUTHORIZATION_POPUP_FEATURES = 'width=1000,height=800';
const NATIVE_OAUTH_RESULT_CHANNEL = 'bustly:native-oauth-result';
const NATIVE_OAUTH_DETACHED_POPUP_GRACE_MS = 60 * 1000;
const nativeOAuthResultChannelName = (flow?: string | null): string => (
    flow ? `${NATIVE_OAUTH_RESULT_CHANNEL}:${flow}` : NATIVE_OAUTH_RESULT_CHANNEL
);

const createNativeOAuthFlowId = (): string => {
    try {
        if (window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }
    } catch {
        // Fall back below when browser crypto is unavailable.
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizeZernioUserMessage = (message?: string): string => {
    if (!message) {
        return 'Authorization failed. Please try again.';
    }

    if (/no_facebook_pages/i.test(message)) {
        return 'No Facebook Pages were available for this Meta account. Make sure the Meta user manages a Facebook Page and grants Bustly access to Pages permissions.';
    }
    if (/window was closed|authorization.*closed/i.test(message)) {
        return 'Authorization was cancelled before Bustly could finish connecting the account.';
    }

    return message
        .replace(/Zernio/gi, 'Bustly')
        .replace(/Zerinio/gi, 'Bustly')
        .replace(/zerinio/gi, 'Bustly')
        .replace(/zernio/gi, 'Bustly');
};

const writeAuthorizationLoading = (popup: Window) => {
    try {
        popup.document.open();
        popup.document.write(buildWaitingPageHtml({
            ...getBrowserWaitingPageContent('authorization'),
            documentTitle: 'Bustly Authorization',
        }));
        popup.document.close();
    } catch {
        // Cross-browser popup handling can reject document writes; the auth redirect still proceeds.
    }
};

const buildJudgeMeLoginAuthorizeUrl = (authorizeUrl: string): string => {
    const loginUrl = new URL('https://app.judge.me/login');
    loginUrl.searchParams.set('type', 'shop');
    loginUrl.searchParams.set('redirect_to', authorizeUrl);
    return loginUrl.toString();
};

const openAuthorizationPopup = (blockedMessage: string): Window => {
    const popup = window.open('about:blank', '_blank', AUTHORIZATION_POPUP_FEATURES);
    if (!popup) {
        throw new Error(blockedMessage);
    }
    writeAuthorizationLoading(popup);
    try { popup.focus(); } catch {}
    return popup;
};

const closeAuthorizationPopup = (popup: Window | null | undefined) => {
    try {
        if (popup && !popup.closed) {
            popup.close();
        }
    } catch {
        // Best-effort cleanup only.
    }
};

const isPopupClosed = (popup: Window): boolean => {
    try {
        return popup.closed || typeof popup.closed === 'undefined';
    } catch {
        return false;
    }
};

const createNangoPopupProxy = (popup: Window): Window => {
    let closedByProxy = false;
    let closedBeforeNavigation = false;
    let navigationFailed = false;
    let nangoNavigatedPopup = false;

    const proxy = {
        get closed() {
            if (closedByProxy || closedBeforeNavigation || navigationFailed) {
                return true;
            }

            if (nangoNavigatedPopup) {
                return false;
            }

            return isPopupClosed(popup);
        },
        get location() {
            return popup.location;
        },
        set location(value: string | Location) {
            closedBeforeNavigation = isPopupClosed(popup);
            if (closedBeforeNavigation) {
                return;
            }

            try {
                popup.location.replace(String(value));
                nangoNavigatedPopup = true;
                return;
            } catch {
                try {
                    popup.location = value as any;
                    nangoNavigatedPopup = true;
                    return;
                } catch {
                    navigationFailed = true;
                }
            }
        },
        close() {
            closedByProxy = true;
            closeAuthorizationPopup(popup);
        },
        focus() {
            try { popup.focus(); } catch {}
        },
    };

    return proxy as unknown as Window;
};

const runWithPreopenedNangoPopup = async <T,>(
    popup: Window,
    startAuth: () => Promise<T>
): Promise<T> => {
    // Nango's auth() opens a blank popup synchronously before it awaits its websocket handshake.
    // In Safari that popup must be the one created directly from our user's click handler.
    // Chrome can also report the real Window handle as closed right after the cross-origin
    // Nango redirect, so the SDK receives a small proxy that keeps that handle stable.
    const nangoPopup = createNangoPopupProxy(popup);
    const originalOpen = window.open;
    const patchedOpen = ((url?: string | URL, target?: string, features?: string) => {
        if ((url === '' || url === undefined) && target === '_blank') {
            return nangoPopup;
        }
        return originalOpen.call(window, url as any, target, features);
    }) as typeof window.open;

    window.open = patchedOpen;
    let authPromise!: Promise<T>;
    try {
        authPromise = startAuth();
    } finally {
        window.open = originalOpen;
    }
    return authPromise;
};

const zernioMessageOrigins = (): Set<string> => {
    const origins = new Set<string>();
    if (typeof window !== 'undefined') {
        origins.add(window.location.origin);
    }

    for (const value of [
        import.meta.env.VITE_INTEGRATIONS_API_HOST,
        import.meta.env.VITE_OAUTH_HOST,
        import.meta.env.VITE_API_HOST,
        import.meta.env.VITE_SUPABASE_URL,
    ]) {
        if (!value) continue;
        try {
            origins.add(new URL(value).origin);
        } catch {
            // Optional env values may be unset or relative in local builds.
        }
    }

    return origins;
};

const parseJudgeMeOAuthResult = (href: string): NativeOAuthPopupResult | null => {
    try {
        const callbackUrl = new URL(href);
        const result = callbackUrl.searchParams.get('judgeme_oauth');
        if (!result) return null;
        const error = callbackUrl.searchParams.get('judgeme_error');
        return {
            connected: result === '1',
            success: result === '1',
            flow: callbackUrl.searchParams.get('judgeme_flow'),
            platform: callbackUrl.searchParams.get('judgeme_platform') || undefined,
            connection: callbackUrl.searchParams.get('judgeme_connection'),
            account_name: callbackUrl.searchParams.get('judgeme_account'),
            error,
        };
    } catch {
        return null;
    }
};

const buildJudgeMeOAuthReturnUri = (flowId?: string): string => {
    const url = new URL(`${window.location.origin}/admin/judgeme/oauth/callback`);
    if (flowId) {
        url.searchParams.set('judgeme_flow', flowId);
    }
    return url.toString();
};

const waitForNativeOAuthPopupResult = (
    popup: Window,
    providerLabel = 'Authorization',
    timeoutMs = 300000,
    options: { expectedFlowId?: string; expectedState?: string; detachedPopupGraceMs?: number } = {}
): Promise<NativeOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        const allowedOrigins = zernioMessageOrigins();
        const detachedPopupGraceMs = options.detachedPopupGraceMs ?? NATIVE_OAUTH_DETACHED_POPUP_GRACE_MS;
        let settled = false;
        let intervalId = 0;
        let channel: BroadcastChannel | null = null;
        let popupClosedSince: number | null = null;

        const cleanup = () => {
            if (intervalId) window.clearInterval(intervalId);
            window.removeEventListener('message', handleMessage);
            if (channel) {
                channel.close();
                channel = null;
            }
        };

        const resolveOnce = (result: NativeOAuthPopupResult) => {
            if (settled) return;
            settled = true;
            cleanup();
            try { popup.close(); } catch {}
            resolve(result);
        };

        const rejectOnce = (error: Error) => {
            if (settled) return;
            settled = true;
            cleanup();
            try { popup.close(); } catch {}
            reject(error);
        };

        const handleOAuthResultData = (value: unknown) => {
            const data = value && typeof value === 'object'
                ? value as NativeOAuthPopupResult & { type?: string }
                : null;
            if (!data || data.type !== 'bustly:native-oauth-result') return;
            if (options.expectedFlowId && data.flow !== options.expectedFlowId) return;
            if (options.expectedState && data.state && data.state !== options.expectedState) return;
            if (data.error || data.success === false) {
                rejectOnce(new Error(`${providerLabel} authorization failed: ${data.error || 'unknown error'}`));
                return;
            }
            resolveOnce({ ...data, connected: data.connected ?? true, success: data.success ?? true });
        };

        const handleMessage = (event: MessageEvent) => {
            if (!allowedOrigins.has(event.origin)) return;
            handleOAuthResultData(event.data);
        };

        window.addEventListener('message', handleMessage);
        try {
            channel = new BroadcastChannel(nativeOAuthResultChannelName(options.expectedFlowId));
            channel.onmessage = (event) => handleOAuthResultData(event.data);
        } catch {
            channel = null;
        }

        intervalId = window.setInterval(() => {
            try {
                if (popup.closed) {
                    popupClosedSince = popupClosedSince || Date.now();
                    if (Date.now() - popupClosedSince >= detachedPopupGraceMs) {
                        rejectOnce(new Error(`${providerLabel} authorization window was closed`));
                    }
                    return;
                }
                popupClosedSince = null;
            } catch {
                popupClosedSince = null;
            }

            if (Date.now() - startedAt > timeoutMs) {
                rejectOnce(new Error(`${providerLabel} authorization timeout`));
                return;
            }

            try {
                const href = popup.location.href;
                if (!href) {
                    return;
                }

                const callbackUrl = new URL(href);
                const oauthError = callbackUrl.searchParams.get('error');
                const code = callbackUrl.searchParams.get('code');
                const state = callbackUrl.searchParams.get('state');
                const flow = callbackUrl.searchParams.get('judgeme_flow');
                const judgeMeResult = parseJudgeMeOAuthResult(href);

                if (oauthError) {
                    rejectOnce(new Error(`${providerLabel} authorization failed: ${oauthError}`));
                    return;
                }

                if (judgeMeResult) {
                    if (options.expectedFlowId && judgeMeResult.flow !== options.expectedFlowId) {
                        return;
                    }
                    if (judgeMeResult.connected) {
                        resolveOnce(judgeMeResult);
                    } else {
                        rejectOnce(new Error(`${providerLabel} authorization failed: ${judgeMeResult.error || 'unknown error'}`));
                    }
                    return;
                }

                if (code && state) {
                    if (options.expectedState && state !== options.expectedState) {
                        return;
                    }
                    resolveOnce({ code, state, flow });
                }
            } catch {
                // Cross-origin phase, ignore until redirected back.
            }
        }, 500);
    });
};

const waitForSlackOAuthPopupResult = (popup: Window, timeoutMs = 300000): Promise<SlackOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        const intervalId = window.setInterval(() => {
            if (popup.closed) {
                window.clearInterval(intervalId);
                reject(new Error('Slack authorization window was closed'));
                return;
            }

            if (Date.now() - startedAt > timeoutMs) {
                window.clearInterval(intervalId);
                popup.close();
                reject(new Error('Slack authorization timeout'));
                return;
            }

            try {
                const href = popup.location.href;
                if (!href) {
                    return;
                }

                const callbackUrl = new URL(href);
                const oauthError = callbackUrl.searchParams.get('error');
                const code = callbackUrl.searchParams.get('code');
                const state = callbackUrl.searchParams.get('state');

                if (oauthError) {
                    window.clearInterval(intervalId);
                    popup.close();
                    reject(new Error(`Slack authorization failed: ${oauthError}`));
                    return;
                }

                if (code && state) {
                    window.clearInterval(intervalId);
                    popup.close();
                    resolve({ code, state });
                }
            } catch {
                // Cross-origin phase, ignore until redirected back.
            }
        }, 500);
    });
};

const waitForZernioOAuthPopupResult = (popup: Window, timeoutMs = 300000): Promise<ZernioOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        let popupClosedSince: number | null = null;
        const timeout = window.setTimeout(() => {
            window.removeEventListener('message', handleMessage);
            try { popup.close(); } catch {}
            reject(new Error('Authorization timed out. Please try again.'));
        }, timeoutMs);

        const intervalId = window.setInterval(() => {
            if (popup.closed) {
                if (popupClosedSince === null) {
                    popupClosedSince = Date.now();
                    return;
                }
                if (Date.now() - popupClosedSince < 2000) {
                    return;
                }
                window.clearInterval(intervalId);
                window.clearTimeout(timeout);
                window.removeEventListener('message', handleMessage);
                reject(new Error('Authorization window was closed before the connection finished.'));
                return;
            }
            popupClosedSince = null;
        }, 500);

        function cleanup() {
            window.clearTimeout(timeout);
            window.clearInterval(intervalId);
            window.removeEventListener('message', handleMessage);
        }

        function handleMessage(event: MessageEvent) {
            const data = event.data;
            if (event.source !== popup) {
                return;
            }
            if (!zernioMessageOrigins().has(event.origin)) {
                return;
            }
            if (!data || data.source !== 'bustly-connection-hub') {
                return;
            }
            cleanup();
            if (data.type === 'SUCCESS') {
                resolve({
                    type: 'SUCCESS',
                    flowId: data.flowId,
                    platform: data.platform,
                    message: data.message,
                });
                return;
            }
            reject(new Error(normalizeZernioUserMessage(data.message)));
        }

        window.addEventListener('message', handleMessage);
    });
};

const waitForFeishuOAuthPopupResult = (popup: Window, timeoutMs = 300000): Promise<FeishuOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        let settled = false;
        const timeout = window.setTimeout(() => {
            cleanup();
            try { popup.close(); } catch {}
            reject(new Error('Feishu authorization timed out. Please try again.'));
        }, timeoutMs);

        // Feishu's cross-origin authorization page can make Chromium report
        // popup.closed=true to the opener while the authorization window is
        // still visible. Treat only explicit callback messages or timeout as
        // terminal states for this flow.
        let channel: BroadcastChannel | null = null;
        try {
            channel = new BroadcastChannel('connect');
            channel.addEventListener('message', handleChannelMessage);
        } catch {
            channel = null;
        }

        function cleanup() {
            window.clearTimeout(timeout);
            window.removeEventListener('message', handleMessage);
            if (channel) {
                try {
                    channel.removeEventListener('message', handleChannelMessage);
                    channel.close();
                } catch {}
                channel = null;
            }
        }

        function handleFeishuResult(data: unknown) {
            if (settled) return;
            const payload = data && typeof data === 'object'
                ? data as FeishuOAuthPopupResult
                : null;
            if (!payload || payload.source !== 'bustly-feishu' || payload.provider !== 'feishu') return;
            settled = true;
            cleanup();
            if (payload.type === 'SUCCESS') {
                resolve(payload);
                return;
            }
            reject(new Error(payload.message || 'Feishu authorization failed'));
        }

        function handleChannelMessage(event: MessageEvent) {
            handleFeishuResult(event.data);
        }

        function handleMessage(event: MessageEvent) {
            if (event.source !== popup) return;
            if (!zernioMessageOrigins().has(event.origin)) return;
            handleFeishuResult(event.data);
        }

        window.addEventListener('message', handleMessage);
    });
};

const waitForDingTalkOAuthPopupResult = (popup: Window, timeoutMs = 300000): Promise<DingTalkOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        let settled = false;
        const timeout = window.setTimeout(() => {
            cleanup();
            try { popup.close(); } catch {}
            reject(new Error('DingTalk authorization timed out. Please try again.'));
        }, timeoutMs);
        let channel: BroadcastChannel | null = null;
        try {
            channel = new BroadcastChannel('connect');
            channel.addEventListener('message', handleChannelMessage);
        } catch {
            channel = null;
        }

        function cleanup() {
            window.clearTimeout(timeout);
            window.removeEventListener('message', handleMessage);
            if (channel) {
                try {
                    channel.removeEventListener('message', handleChannelMessage);
                    channel.close();
                } catch {}
                channel = null;
            }
        }

        function handleDingTalkResult(data: unknown) {
            if (settled) return;
            const payload = data && typeof data === 'object'
                ? data as DingTalkOAuthPopupResult
                : null;
            if (!payload || payload.source !== 'bustly-dingtalk' || payload.provider !== 'dingtalk') return;
            settled = true;
            cleanup();
            if (payload.type === 'SUCCESS') {
                resolve(payload);
                return;
            }
            reject(new Error(payload.message || 'DingTalk authorization failed'));
        }

        function handleChannelMessage(event: MessageEvent) {
            handleDingTalkResult(event.data);
        }

        function handleMessage(event: MessageEvent) {
            if (event.source !== popup) return;
            if (!zernioMessageOrigins().has(event.origin)) return;
            handleDingTalkResult(event.data);
        }

        window.addEventListener('message', handleMessage);
    });
};

/**
 * 从 workspace API 拉取和管理当前 workspace 的集成状态
 */
export const useWorkspaceIntegrations = (
    workspaceId?: string,
    options: UseWorkspaceIntegrationsOptions = {}
): UseWorkspaceIntegrationsReturn => {
    const { enabled = true, requireFreshFetch = false } = options;
    const initialCachedIntegrations = readCachedWorkspaceIntegrations(workspaceId);
    const [allIntegrations, setAllIntegrations] = useState<WorkspaceIntegration[]>(initialCachedIntegrations);
    const [isLoading, setIsLoading] = useState(enabled && initialCachedIntegrations.length === 0);
    const [error, setError] = useState<Error | null>(null);
    const activeFetchScopeRef = useRef<WorkspaceIntegrationFetchScope>(
        createWorkspaceIntegrationFetchScope(enabled ? workspaceId : undefined)
    );
    const [freshFetchScope, setFreshFetchScope] = useState<WorkspaceIntegrationFetchScope | null>(null);
    const renderedWorkspaceId = enabled && workspaceId ? workspaceId : null;
    const hasFetchedCurrentWorkspace = Boolean(
        renderedWorkspaceId
        && activeFetchScopeRef.current.workspaceId === renderedWorkspaceId
        && hasFreshWorkspaceIntegrationSnapshot(
            activeFetchScopeRef.current,
            freshFetchScope
        )
    );

    /**
     * 移除集成
     */
    const removeIntegration = useCallback((provider: string) => {
        const normalizedProvider = provider.toLowerCase().replace(/[\s_]+/g, '-');
        const providerKey = normalizedProvider.startsWith('figma-') ? 'figma' : normalizedProvider;
        const targetKey = PROVIDER_TO_KEY[providerKey] || providerKey;
        setAllIntegrations(prev => prev.filter((integration) => {
            const normalizedIntegrationProvider = integration.provider.toLowerCase().replace(/[\s_]+/g, '-');
            const integrationProviderKey = normalizedIntegrationProvider.startsWith('figma-')
                ? 'figma'
                : normalizedIntegrationProvider;
            const integrationKey = PROVIDER_TO_KEY[integrationProviderKey] || integrationProviderKey;
            return integrationKey !== targetKey;
        }));
    }, []);

    const buildWorkspaceAxiosRequestConfig = useCallback((): AxiosRequestConfig => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const baseAxiosRequestConfig: AxiosRequestConfig = { headers: { 'X-Workspace-Id': workspaceId } };
        return isPluginProduct(getCurrentProductCode())
            ? withPluginProductHeaders(baseAxiosRequestConfig)
            : baseAxiosRequestConfig;
    }, [workspaceId]);

    /**
     * 从 API 拉取当前 workspace 的集成列表
     * @param showLoading - 是否显示 loading 状态，默认为 true
     */
    const fetchIntegrations = useCallback(async (
        showLoading = true,
        options: FetchWorkspaceIntegrationsOptions = {}
    ): Promise<WorkspaceIntegration[]> => {
        const forceRefresh = options.forceRefresh === true;
        const requestScope = activeFetchScopeRef.current;
        const isRequestForActiveWorkspace = () => (
            requestScope.workspaceId === workspaceId
            && isSameWorkspaceIntegrationFetchScope(activeFetchScopeRef.current, requestScope)
        );
        if (!workspaceId) {
            const empty: WorkspaceIntegration[] = [];
            setAllIntegrations(empty);
            setIsLoading(false);
            setError(null);
            return empty;
        }

        const cachedIntegrations = readCachedWorkspaceIntegrations(workspaceId);
        const inFlightEntry = forceRefresh ? undefined : workspaceIntegrationsInFlight.get(workspaceId);
        if (inFlightEntry) {
            if (isRequestForActiveWorkspace() && showLoading && cachedIntegrations.length === 0) {
                setIsLoading(true);
            }
            const result = await inFlightEntry.promise;
            if (inFlightEntry.superseded) {
                if (!isRequestForActiveWorkspace()) {
                    return readCachedWorkspaceIntegrations(workspaceId);
                }
                return fetchIntegrations(showLoading, { forceRefresh: false });
            }

            if (isRequestForActiveWorkspace()) {
                if (result.error) {
                    setError(result.error);
                    setFreshFetchScope(null);
                } else {
                    setAllIntegrations((previousIntegrations) => {
                        const nextIntegrations = preserveGoogleAdsExternalAccountNames(
                            result.integrations,
                            previousIntegrations
                        );
                        workspaceIntegrationsCache.set(workspaceId, nextIntegrations);
                        return nextIntegrations;
                    });
                    setError(null);
                    setFreshFetchScope(requestScope);
                }
                if (showLoading) {
                    setIsLoading(false);
                }
            }

            return result.error ? cachedIntegrations : result.integrations;
        }

        const lastFetchedAt = workspaceIntegrationsFetchedAt.get(workspaceId) || 0;
        const isRecentSilentRefresh = !showLoading
            && !forceRefresh
            && nowForTiming() - lastFetchedAt < WORKSPACE_INTEGRATIONS_SILENT_REFRESH_DEDUPE_MS;
        if (isRecentSilentRefresh) {
            if (isRequestForActiveWorkspace()) {
                setAllIntegrations(cachedIntegrations);
                setError(null);
                setFreshFetchScope(requestScope);
            }
            return cachedIntegrations;
        }

        if (isRequestForActiveWorkspace() && showLoading) {
            setIsLoading(true);
        }
        if (isRequestForActiveWorkspace()) {
            setError(null);
        }

        const timingTracker = createWorkspaceIntegrationTimingTracker(workspaceId);
        const previousEntry = workspaceIntegrationsInFlight.get(workspaceId);
        if (forceRefresh && previousEntry) {
            previousEntry.superseded = true;
        }

        const entry: WorkspaceIntegrationsInFlightEntry = {
            superseded: false,
            promise: (async (): Promise<WorkspaceIntegrationsFetchResult> => {
                try {
                    const integrations: WorkspaceIntegration[] = [];
                    const response = await trackPromiseTiming(
                        timingTracker,
                        'workspaceIntegrationsApi',
                        authService.getWorkspaceIntegrations(buildWorkspaceAxiosRequestConfig())
                    );

                    // 直接使用 API 返回的数据格式，不做转换
                    // response 已经是 data 对象了（api.ts 自动提取了 response.data.data）
                    if (response) {
                        Object.entries(response).forEach(([, apiIntegrations]) => {
                            if (Array.isArray(apiIntegrations) && apiIntegrations.length > 0) {
                                apiIntegrations.forEach((apiIntegration: WorkspaceIntegrationAPI) => {
                                    integrations.push({ ...apiIntegration });
                                });
                            }
                        });
                    }

                    return { integrations, error: null };
                } catch (requestError) {
                    const normalizedError = normalizeWorkspaceIntegrationsError(requestError);
                    console.error('Error fetching workspace integrations:', normalizedError);
                    return { integrations: [], error: normalizedError };
                } finally {
                    console.info('[useWorkspaceIntegrations] load timings', timingTracker.toLogPayload());
                }
            })(),
        };

        workspaceIntegrationsInFlight.set(workspaceId, entry);
        const result = await entry.promise;
        const shouldApplyGlobally = (
            !entry.superseded
            && workspaceIntegrationsInFlight.get(workspaceId) === entry
        );

        if (shouldApplyGlobally) {
            if (!result.error) {
                const nextIntegrations = preserveGoogleAdsExternalAccountNames(
                    result.integrations,
                    readCachedWorkspaceIntegrations(workspaceId)
                );
                workspaceIntegrationsCache.set(workspaceId, nextIntegrations);
                workspaceIntegrationsFetchedAt.set(workspaceId, nowForTiming());
            }
            workspaceIntegrationsInFlight.delete(workspaceId);
        }

        if (entry.superseded) {
            if (!isRequestForActiveWorkspace()) {
                return readCachedWorkspaceIntegrations(workspaceId);
            }
            return fetchIntegrations(showLoading, { forceRefresh: false });
        }

        if (isRequestForActiveWorkspace()) {
            if (result.error) {
                setError(result.error);
                setFreshFetchScope(null);
            } else {
                setAllIntegrations((previousIntegrations) => {
                    const nextIntegrations = preserveGoogleAdsExternalAccountNames(
                        result.integrations,
                        previousIntegrations
                    );
                    workspaceIntegrationsCache.set(workspaceId, nextIntegrations);
                    return nextIntegrations;
                });
                setError(null);
                setFreshFetchScope(requestScope);
            }
            if (showLoading) {
                setIsLoading(false);
            }
        }

        return result.error ? cachedIntegrations : result.integrations;
    }, [buildWorkspaceAxiosRequestConfig, workspaceId]);

    // 初始化和 workspace 变化时拉取数据
    useEffect(() => {
        const activeScope = advanceWorkspaceIntegrationFetchScope(
            activeFetchScopeRef.current,
            enabled ? workspaceId : undefined
        );
        activeFetchScopeRef.current = activeScope;
        setFreshFetchScope(null);

        const cachedIntegrations = readCachedWorkspaceIntegrations(workspaceId);
        if (cachedIntegrations.length > 0) {
            setAllIntegrations(cachedIntegrations);
        } else {
            setAllIntegrations([]);
        }

        if (!enabled) {
            setIsLoading(false);
            setError(null);
            return () => {
                if (isSameWorkspaceIntegrationFetchScope(activeFetchScopeRef.current, activeScope)) {
                    activeFetchScopeRef.current = invalidateWorkspaceIntegrationFetchScope(activeScope);
                }
            };
        }

        const hasCachedIntegrations = cachedIntegrations.length > 0;
        const shouldShowLoading = requireFreshFetch || !hasCachedIntegrations;
        setIsLoading(shouldShowLoading);
        void fetchIntegrations(shouldShowLoading, { forceRefresh: requireFreshFetch });

        return () => {
            if (isSameWorkspaceIntegrationFetchScope(activeFetchScopeRef.current, activeScope)) {
                activeFetchScopeRef.current = invalidateWorkspaceIntegrationFetchScope(activeScope);
            }
        };
    // 依赖 workspace 身份和显式 freshness 策略，避免 callback 变化引起重复请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId, enabled, requireFreshFetch]);


    /**
     * 验证集成是否创建成功
     * 通过直接查询 workspace_integrations 表检查状态
     * @param provider - 集成平台 (如 'shopify', 'google-ads', 'slack', 'bigcommerce')
     * @param connectionId - Nango 连接 ID (可选，Shopify 不需要)
     */
    const checkIntegrationCreated = useCallback(async (
        provider: string,
        connectionId?: string,
        options: IntegrationVerificationOptions = {}
    ): Promise<string | undefined> => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const normalizedProvider = provider.toLowerCase().replace(/[\s_]+/g, '-');
        const providerKey = normalizedProvider.startsWith('figma-') ? 'figma' : normalizedProvider;
        if (JUDGEME_OAUTH_PROVIDERS.has(normalizedProvider)) {
            for (let i = 0; i < 8; i++) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                const isConnected = isIntegrationVerificationSatisfied(
                    refreshedIntegrations,
                    normalizedProvider,
                    connectionId,
                    options
                );
                if (isConnected) {
                    return;
                }
            }

            throw new Error(`Failed to create ${provider} integration`);
        }

        // Nango provider 到数据库 platform 的映射
        const PROVIDER_TO_DB_PLATFORM: Record<string, string> = {
            'shopify': 'SHOPIFY',
            'google-ads': 'GOOGLE_ADS',
            'google-analytics': 'GOOGLE_ANALYTICS',
            'tiktok-ads': 'TIKTOK_ADS',
            'google-search-console': 'GOOGLE_SEARCH_CONSOLE',
            'semrush': 'SEMRUSH',
            'slack': 'SLACK',
            'bigcommerce': 'BIGCOMMERCE',
            'woocommerce': 'WOOCOMMERCE',
            'magento': 'MAGENTO',
            'adobe-commerce': 'MAGENTO',  // Nango 的 adobe-commerce 对应数据库的 MAGENTO
            'prestashop': 'PRESTASHOP',
            'presta-shop': 'PRESTASHOP',
            'aliexpress': 'ALIEXPRESS',
            'klaviyo': 'KLAVIYO',
            'klaviyo-oauth': 'KLAVIYO',
            'mailchimp': 'MAILCHIMP',
            'mailchimp-oauth': 'MAILCHIMP',
            'feishu': 'FEISHU',
            'dingtalk': 'DINGTALK',
            'ding-talk': 'DINGTALK',
            'dingding': 'DINGTALK',
            'wix': 'WIX',
            'meta-ads': 'META_ADS',
            'metaads': 'META_ADS',
            'facebook-page': 'FACEBOOK_PAGE',
            'facebook': 'FACEBOOK_PAGE',
            'instagram': 'INSTAGRAM',
            'similarweb': 'SIMILARWEB',
            'ahrefs': 'AHREFS',
            'omnisend': 'OMNISEND',
            'okendo': 'OKENDO',
            'insider-one': 'INSIDER_ONE',
            'insider-one-oauth': 'INSIDER_ONE',
            'insider': 'INSIDER_ONE',
            'useinsider': 'INSIDER_ONE',
            'emarsys': 'EMARSYS',
            'emarsys-oauth': 'EMARSYS',
            'sap-emarsys': 'EMARSYS',
            'sap-engagement-cloud': 'EMARSYS',
            'linkedin-ads': 'LINKEDIN_ADS',
            'linkedinads': 'LINKEDIN_ADS',
            'linkedin': 'LINKEDIN_ADS',
            'pinterest-ads': 'PINTEREST_ADS',
            'pinterestads': 'PINTEREST_ADS',
            'pinterest': 'PINTEREST_ADS',
            'x-ads': 'X_ADS',
            'xads': 'X_ADS',
            'twitter-ads': 'X_ADS',
            'twitterads': 'X_ADS',
            'twitter-x': 'X_ADS',
            'twitterx': 'X_ADS',
            'twitter': 'X_ADS',
            'x': 'X_ADS',
            'youtube-ads': 'YOUTUBE',
            'youtubeads': 'YOUTUBE',
            'youtube': 'YOUTUBE',
            'reddit': 'REDDIT',
            'reddit-social': 'REDDIT',
            'attribuly': 'ATTRIBULY',
            'attribuly-attribution': 'ATTRIBULY',
            'snapchat-ads': 'SNAPCHAT',
            'snapchatads': 'SNAPCHAT',
            'snapchat': 'SNAPCHAT',
            'judgeme': 'JUDGEME',
            'judge-me': 'JUDGEME',
            'judge.me': 'JUDGEME',
            'judge': 'JUDGEME',
            'figma': 'FIGMA',
            'figma-oauth': 'FIGMA',
            'noxinfluencer': 'NOXINFLUENCER',
            'nox-influencer': 'NOXINFLUENCER',
            'nox': 'NOXINFLUENCER',
        };
        const PROVIDER_TO_DB_PLATFORM_ALIASES: Record<string, string[]> = {
            'meta-ads': ['META_ADS'],
            'metaads': ['META_ADS'],
            'facebook-page': ['FACEBOOK_PAGE'],
            'facebook': ['FACEBOOK_PAGE'],
            'instagram': ['INSTAGRAM'],
            'linkedin-ads': ['LINKEDIN_ADS'],
            'linkedinads': ['LINKEDIN_ADS'],
            'linkedin': ['LINKEDIN_ADS'],
            'pinterest-ads': ['PINTEREST_ADS'],
            'pinterestads': ['PINTEREST_ADS'],
            'pinterest': ['PINTEREST_ADS'],
            'x-ads': ['X_ADS'],
            'xads': ['X_ADS'],
            'twitter-ads': ['X_ADS'],
            'twitterads': ['X_ADS'],
            'twitter-x': ['X_ADS'],
            'twitterx': ['X_ADS'],
            'twitter': ['X_ADS'],
            'x': ['X_ADS'],
            'youtube-ads': ['YOUTUBE'],
            'youtubeads': ['YOUTUBE'],
            'youtube': ['YOUTUBE'],
            'reddit': ['REDDIT'],
            'reddit-social': ['REDDIT'],
            'attribuly': ['ATTRIBULY'],
            'attribuly-attribution': ['ATTRIBULY'],
            'snapchat-ads': ['SNAPCHAT'],
            'snapchatads': ['SNAPCHAT'],
            'snapchat': ['SNAPCHAT'],
            'mailchimp': ['MAILCHIMP'],
            'mailchimp-oauth': ['MAILCHIMP'],
            'insider-one': ['INSIDER_ONE'],
            'insider-one-oauth': ['INSIDER_ONE'],
            'insider': ['INSIDER_ONE'],
            'useinsider': ['INSIDER_ONE'],
            'emarsys': ['EMARSYS'],
            'emarsys-oauth': ['EMARSYS'],
            'sap-emarsys': ['EMARSYS'],
            'sap-engagement-cloud': ['EMARSYS'],
            'okendo': ['OKENDO'],
            'judgeme': ['JUDGEME'],
            'judge-me': ['JUDGEME'],
            'judge.me': ['JUDGEME'],
            'judge': ['JUDGEME'],
            'feishu': ['FEISHU'],
            'dingtalk': ['DINGTALK'],
            'ding-talk': ['DINGTALK'],
            'dingding': ['DINGTALK'],
            'figma': ['FIGMA'],
            'figma-oauth': ['FIGMA'],
            'noxinfluencer': ['NOXINFLUENCER'],
            'nox-influencer': ['NOXINFLUENCER'],
            'nox': ['NOXINFLUENCER'],
        };

        const platform = PROVIDER_TO_DB_PLATFORM[providerKey] || providerKey.toUpperCase().replace(/-/g, '_');
        const platformCandidates = PROVIDER_TO_DB_PLATFORM_ALIASES[providerKey] || [platform];
        const isZernioProvider = ZERNIO_PROVIDERS.has(providerKey);
        const webhookBackedProviders = new Set(['woocommerce', 'magento', 'adobe-commerce', 'klaviyo', 'klaviyo-oauth', 'mailchimp', 'mailchimp-oauth', 'semrush', 'insider-one', 'insider-one-oauth', 'emarsys', 'emarsys-oauth', 'judgeme', 'judge-me', 'judge.me', 'figma', 'figma-oauth', 'google-search-console']);
        const delayedActivationProviders = new Set(['shopify', 'wix']);
        const canWaitForActivation =
            delayedActivationProviders.has(providerKey)
            || webhookBackedProviders.has(providerKey)
            || isZernioProvider;
        const maxAttempts = providerKey === 'google-ads' || isZernioProvider
            ? 45
            : delayedActivationProviders.has(providerKey) ? 45
            : webhookBackedProviders.has(providerKey) ? 8 : 3;

        for (let i = 0; i < maxAttempts; i++) {
            // 等待 1 秒
            await new Promise(resolve => setTimeout(resolve, 1000));

            // 构建 Supabase 查询
            let query = supabase
                .from('workspace_integrations')
                .select('*')
                .eq('workspace_id', workspaceId);

            query = platformCandidates.length > 1
                ? query.in('platform', platformCandidates)
                : query.eq('platform', platformCandidates[0]);

            // Native BYO custom app OAuth returns workspace_integrations.slug_id.
            const isNativeCustomAppProvider = provider === 'feishu' || provider === 'dingtalk';
            if (connectionId && isNativeCustomAppProvider) {
                query = query.eq('slug_id', connectionId);
            } else if (isNativeCustomAppProvider) {
                query = query.eq('status', 'ACTIVE');
            }

            // 如果有 connectionId，则添加 nango_connection_id 过滤条件
            if (connectionId) {
                if (!isNativeCustomAppProvider) {
                    query = query.eq('nango_connection_id', connectionId);
                }
            }

            const { data, error } = await query
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error checking integration status:', error);
                continue;
            }

            // 找到记录，检查状态
            if (data) {
                const status = String(data.status || '').toUpperCase();
                if (status === 'ACTIVE') {
                    const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                    if (isIntegrationVerificationSatisfied(
                        refreshedIntegrations,
                        providerKey,
                        connectionId,
                        options
                    )) {
                        return;
                    }
                    // The row can belong to a pre-existing account for the same provider.
                    // Keep polling until the requested/new instance appears in the API snapshot.
                    continue;
                }

                if (status === 'FAILED' || status === 'ERROR') {
                    const errorMsg = data.error_message || `Integration ${provider} failed with status: ${data.status}`;
                    throw new Error(isZernioProvider ? normalizeZernioUserMessage(errorMsg) : errorMsg);
                }

                if (!canWaitForActivation || i === maxAttempts - 1) {
                    // 状态为非 ACTIVE，抛出错误信息
                    const errorMsg = data.error_message || `Integration ${provider} failed with status: ${data.status}`;
                    throw new Error(isZernioProvider ? normalizeZernioUserMessage(errorMsg) : errorMsg);
                }
            }

            if (isZernioProvider) {
                const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                if (isIntegrationVerificationSatisfied(
                    refreshedIntegrations,
                    providerKey,
                    connectionId,
                    options
                )) {
                    return;
                }
            }
        }

        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        if (isIntegrationVerificationSatisfied(
            refreshedIntegrations,
            providerKey,
            connectionId,
            options
        )) {
            return;
        }

        throw new Error(`Failed to create ${provider} integration`);
    }, [fetchIntegrations, workspaceId]);

    const waitForGoogleAdsConnectionCreatedAfter = useCallback(async (
        startedAtIso: string,
        options: {
            maxAttempts?: number;
            shouldStop?: () => boolean;
        } = {}
    ): Promise<string> => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const maxAttempts = options.maxAttempts ?? 300;
        const startedAtMs = Date.parse(startedAtIso);
        const thresholdMs = Number.isFinite(startedAtMs) ? startedAtMs - 10000 : Date.now() - 10000;

        for (let i = 0; i < maxAttempts; i++) {
            if (options.shouldStop?.()) {
                throw new Error('Google Ads authorization recovery stopped.');
            }

            await new Promise(resolve => setTimeout(resolve, 1000));

            if (options.shouldStop?.()) {
                throw new Error('Google Ads authorization recovery stopped.');
            }

            const { data, error } = await supabase
                .from('workspace_integrations')
                .select('nango_connection_id, connected_at, created_at, updated_at, status')
                .eq('workspace_id', workspaceId)
                .eq('platform', 'GOOGLE_ADS')
                .eq('status', 'ACTIVE')
                .order('connected_at', { ascending: false })
                .limit(5);

            if (error) {
                console.error('Error recovering Google Ads connection status:', error);
                continue;
            }

            const recoveredConnection = (data || []).find((row: any) => {
                const connectionId = String(row?.nango_connection_id || '').trim();
                if (!connectionId) {
                    return false;
                }
                const connectedAtMs = Date.parse(row?.connected_at || row?.updated_at || row?.created_at || '');
                return Number.isFinite(connectedAtMs) && connectedAtMs >= thresholdMs;
            });

            const recoveredConnectionId = String(recoveredConnection?.nango_connection_id || '').trim();
            if (recoveredConnectionId) {
                await fetchIntegrations(false, { forceRefresh: true });
                return recoveredConnectionId;
            }
        }

        throw new Error('Google Ads authorization did not finish. Please refresh and try again.');
    }, [fetchIntegrations, workspaceId]);

    const waitForWorkspaceIntegrationCreatedAfter = useCallback(async (
        provider: string,
        baseline: ProviderConnectionBaseline,
        options: {
            maxAttempts?: number;
            shouldStop?: () => boolean;
        } = {}
    ): Promise<void> => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const maxAttempts = options.maxAttempts ?? 300;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (options.shouldStop?.()) {
                throw new Error(`${provider} authorization recovery stopped.`);
            }

            await new Promise(resolve => setTimeout(resolve, 1000));

            if (options.shouldStop?.()) {
                throw new Error(`${provider} authorization recovery stopped.`);
            }

            const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
            if (isIntegrationVerificationSatisfied(
                refreshedIntegrations,
                provider,
                undefined,
                { baseline, requireNewConnection: true }
            )) {
                return resolveNewProviderConnectionId(refreshedIntegrations, provider, baseline);
            }
        }

        throw new Error(`${provider} authorization did not finish. Please refresh and try again.`);
    }, [fetchIntegrations, workspaceId]);

    const checkZernioIntegrationCreated = useCallback(async (
        provider: string,
        flowId?: string,
        axiosRequestConfig?: AxiosRequestConfig,
        options: {
            maxAttempts?: number;
            shouldStop?: () => boolean;
            verification?: IntegrationVerificationOptions;
        } = {}
    ): Promise<void> => {
        if (!flowId) {
            await checkIntegrationCreated(provider, undefined, options.verification);
            return;
        }

        const maxAttempts = options.maxAttempts ?? 120;

        for (let i = 0; i < maxAttempts; i++) {
            if (options.shouldStop?.()) {
                throw new Error('Authorization was cancelled before the connection finished.');
            }

            await new Promise(resolve => setTimeout(resolve, 1000));

            if (options.shouldStop?.()) {
                throw new Error('Authorization was cancelled before the connection finished.');
            }

            const status = await authService.getZernioConnectStatus(flowId, axiosRequestConfig);
            const flowStatus = String(status.status || '').toUpperCase();
            const integrationStatus = String(status.integrationStatus || '').toUpperCase();
            const failureMessage = status.integrationErrorMessage
                || status.errorMessage
                || `Integration ${provider} failed with status: ${status.integrationStatus || status.status || 'unknown'}`;

            if (flowStatus === 'FAILED' || flowStatus === 'ERROR' || integrationStatus === 'FAILED' || integrationStatus === 'ERROR') {
                throw new Error(normalizeZernioUserMessage(failureMessage));
            }

            if (flowStatus === 'COMPLETED' || integrationStatus === 'ACTIVE') {
                const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                if (isIntegrationVerificationSatisfied(
                    refreshedIntegrations,
                    provider,
                    undefined,
                    options.verification
                )) {
                    return;
                }
                if (flowStatus === 'COMPLETED' && !options.verification?.requireNewConnection) {
                    return;
                }
            }
        }

        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        if (isIntegrationVerificationSatisfied(
            refreshedIntegrations,
            provider,
            undefined,
            options.verification
        )) {
            return;
        }

        throw new Error(`Failed to create ${provider} integration`);
    }, [checkIntegrationCreated, fetchIntegrations]);

    /**
     * 将集成列表按平台分组，只返回每个平台的第一个已连接集成
     */
    const integrations: WorkspaceIntegrationsState = useMemo(() => {
        const result: WorkspaceIntegrationsState = {
            shopify: null,
            bigCommerce: null,
            googleAds: null,
            googleAnalytics: null,
            tiktokAds: null,
            googleSearchConsole: null,
            semrush: null,
            slack: null,
            woocommerce: null,
            magento: null,
            prestashop: null,
            aliexpress: null,
            klaviyo: null,
            mailchimp: null,
            wix: null,
            metaAds: null,
            facebookPage: null,
            instagram: null,
            similarweb: null,
            ahrefs: null,
            omnisend: null,
            okendo: null,
            goaffpro: null,
            uppromote: null,
            loox: null,
            trustwill: null,
            noxinfluencer: null,
            insiderOne: null,
            emarsys: null,
            judgeme: null,
            linkedinAds: null,
            pinterestAds: null,
            xAds: null,
            youtube: null,
            reddit: null,
            attribuly: null,
            snapchat: null,
            feishu: null,
            dingtalk: null,
            figma: null,
        };

        allIntegrations.forEach((integration) => {
            const key = PROVIDER_TO_KEY[integration.provider];
            if (key && integration.connected && !result[key]) {
                result[key] = integration;
            }
        });

        return result;
    }, [allIntegrations]);

    /**
     * 检查指定平台是否已连接
     */
    const isConnected = useCallback((platform: string): boolean => {
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');
        const targetKey = PROVIDER_TO_KEY[normalizedProvider] || normalizedProvider;
        return allIntegrations.some(
            (integration) => integration.connected
                && ((PROVIDER_TO_KEY[integration.provider] || integration.provider) === targetKey)
        );
    }, [allIntegrations]);

    /**
     * 获取指定平台的集成信息
     */
    const getIntegration = useCallback((platform: string): WorkspaceIntegration | null => {
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');
        const targetKey = PROVIDER_TO_KEY[normalizedProvider] || normalizedProvider;
        return (
            allIntegrations.find(
                (integration) => integration.connected
                    && ((PROVIDER_TO_KEY[integration.provider] || integration.provider) === targetKey)
            ) || null
        );
    }, [allIntegrations]);

    /**
     * 检查是否有任何已连接的集成（排除 slack）
     */
    const hasConnectedIntegrations = useMemo(() => {
        return allIntegrations.some(
            (integration) => integration.connected && integration.provider !== 'slack'
        );
    }, [allIntegrations]);

    const resolveConnectionScene = useCallback((connectionConfig?: any): ConnectionScene => {
        if (connectionConfig?.connection_scene === 'onboarding' || connectionConfig?.connection_scene === 'admin') {
            return connectionConfig.connection_scene;
        }

        return connectionConfig?.onboarding_flow ? 'onboarding' : 'admin';
    }, []);

    const buildSceneRequest = useCallback((connectionConfig?: any) => {
        const connectionScene = resolveConnectionScene(connectionConfig);
        return {
            connection_scene: connectionScene,
            onboarding_flow: connectionConfig?.onboarding_flow ?? connectionScene === 'onboarding',
        };
    }, [resolveConnectionScene]);

    const resolveConnectionLabel = useCallback((connectionConfig?: any) => {
        const label = connectionConfig?.label ?? connectionConfig?.connection_alias;
        return typeof label === 'string' ? label.trim() : '';
    }, []);

    const updateConnectedIntegrationLabel = useCallback(async (
        provider: string,
        connectionConfig?: any,
        match: { connectionId?: string; shopDomain?: string } = {}
    ) => {
        const label = resolveConnectionLabel(connectionConfig);
        if (!workspaceId || !label) {
            return;
        }

        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        const mapping = findProviderConnectionMappingForLabel(
            refreshedIntegrations,
            provider,
            match
        );

        if (!mapping?.id) {
            return;
        }

        const axiosRequestConfig = buildWorkspaceAxiosRequestConfig();

        await authService.updateWorkspaceIntegrationLabel(mapping.id, {
            label,
            axiosRequestConfig,
        });
        await fetchIntegrations(false, { forceRefresh: true });
    }, [buildWorkspaceAxiosRequestConfig, fetchIntegrations, resolveConnectionLabel, workspaceId]);

    /**
     * 连接平台集成
     * @param platform - 平台名称（如 'shopify', 'google-ads', 'slack'）
     * @param connectionConfig - 连接配置（可选，如 store_domain, redirect_url 等）
     * @param options - 回调选项（onSuccess, onError）
     */
    const connect = useCallback(async (
        platform: string,
        connectionConfig?: any,
        options?: {
            onAuthorizationStarted?: () => void | Promise<void>;
            onSuccess?: () => void | Promise<void>;
            onError?: (error: string) => void | Promise<void>;
            onRestriction?: (payload: ConnectRestrictionPayload) => void | Promise<void>;
        }
    ) => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const axiosRequestConfig = buildWorkspaceAxiosRequestConfig();

        // 标准化平台名称：同时处理空格和下划线
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');
        // Onboarding can add another instance for any provider in both Core
        // and Plugin. Keep a baseline for the onboarding scene so OAuth and
        // credential verification wait for the newly-created mapping instead
        // of accepting the provider's existing connection.
        const onboardingConnectionBaseline = resolveConnectionScene(connectionConfig) === 'onboarding'
            ? allIntegrations
            : null;

        const buildConnectVerificationOptions = (
            provider: string,
            verificationOptions: Pick<IntegrationVerificationOptions, 'expectedAccount'> = {}
        ): IntegrationVerificationOptions => (
            onboardingConnectionBaseline
                ? {
                    ...verificationOptions,
                    baseline: createProviderConnectionBaseline(onboardingConnectionBaseline, provider),
                    requireNewConnection: true,
                }
                : verificationOptions
        );

        const checkIntegrationCreatedForConnect = (
            provider: string,
            connectionId?: string,
            verificationOptions: Pick<IntegrationVerificationOptions, 'expectedAccount'> = {}
        ) => checkIntegrationCreated(
            provider,
            connectionId,
            buildConnectVerificationOptions(provider, verificationOptions)
        );

        const buildNangoConnectResult = (
            tokenBody: any,
            result: any = {},
            success = false
        ) => ({
            ...result,
            success,
            restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
            blockConnect: tokenBody.blockConnect ?? false,
            upgradeRequired: tokenBody.upgradeRequired ?? false,
            ctaAction: tokenBody.ctaAction,
            eligibilityState: tokenBody.eligibilityState,
            allowsFreshTrial: tokenBody.allowsFreshTrial,
            message: tokenBody.message ?? tokenBody.eligibilityMessage,
            eligibilityMessage: tokenBody.eligibilityMessage,
            trialRemainingDays: tokenBody.trialRemainingDays,
            currentWorkspaceId: tokenBody.currentWorkspaceId,
            currentWorkspaceName: tokenBody.currentWorkspaceName,
            currentStoreCount: tokenBody.currentStoreCount,
            storeLimit: tokenBody.storeLimit,
            upgradeTarget: tokenBody.upgradeTarget,
        });

        const buildConnectRestrictionResultFromError = (error: unknown) => {
            const restriction = resolveRestrictionFromConnectError(error);
            return restriction ? { success: false, ...restriction } : null;
        };

        const shouldStopForSubscriptionRestriction = (payload: ConnectRestrictionPayload) => {
            const restrictionState = payload.restrictionState ?? payload.eligibilityState ?? null;
            return payload.allowsFreshTrial === false
                && (
                    restrictionState === 'trial_valid'
                    || restrictionState === 'trial_expired'
                    || restrictionState === 'plan_expired'
                    || restrictionState === 'payment_past_due'
                    || restrictionState === 'already_bound'
                );
        };

        const verifyNangoConnection = async (
            verificationProvider: string,
            connectionId?: string
        ) => {
            if (!connectionId) {
                throw new Error('The authorization finished without a connection ID. Please try again.');
            }

            try {
                await checkIntegrationCreatedForConnect(verificationProvider, connectionId);
                return null;
            } catch (error) {
                const restriction = resolveRestrictionFromConnectError(error);
                if (restriction) {
                    return { success: false, ...restriction };
                }
                throw error;
            }
        };

        const runNangoAuthorizationWithWorkspaceRecovery = async ({
            authWindow,
            nango,
            provider,
            startAuth,
        }: {
            authWindow: Window;
            nango: Nango;
            provider: string;
            startAuth: () => Promise<any>;
        }) => {
            const baseline = createProviderConnectionBaseline(allIntegrations, provider);
            let stopRecovery = false;
            try {
                const authResultPromise = runWithPreopenedNangoPopup(authWindow, startAuth);
                const recoveredConnectionPromise = waitForWorkspaceIntegrationCreatedAfter(
                    provider,
                    baseline,
                    {
                        maxAttempts: 300,
                        shouldStop: () => stopRecovery,
                    }
                );

                const result = await Promise.race([
                    authResultPromise,
                    recoveredConnectionPromise.then((connectionId) => ({
                        connectionId,
                        recoveredAfterAuthResultMissing: true,
                    })),
                ]);
                if (result?.connectionId || result?.recoveredAfterAuthResultMissing) {
                    return result;
                }

                const connectionId = await recoveredConnectionPromise;
                return {
                    ...result,
                    connectionId,
                    recoveredAfterAuthResultMissing: true,
                };
            } finally {
                stopRecovery = true;
            }
        };

        const connectNangoOAuthDirectly = async ({
            integrationId,
            providerConfigKey,
            verificationProvider,
            displayName,
        }: {
            integrationId: string;
            providerConfigKey: string;
            verificationProvider: string;
            displayName: string;
        }) => {
            const authWindow = openAuthorizationPopup(
                `Unable to open the ${displayName} authorization window. Please allow pop-ups for Bustly and try again.`
            );
            let tokenBody;
            try {
                tokenBody = await authService.getNangoConnectToken({
                    integration_id: integrationId,
                    provider_config_key: providerConfigKey,
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
            } catch (error) {
                closeAuthorizationPopup(authWindow);
                const restrictionResult = buildConnectRestrictionResultFromError(error);
                if (restrictionResult) {
                    return restrictionResult;
                }
                throw error;
            }
            const tokenRestrictionResult = buildNangoConnectResult(tokenBody);
            if (shouldStopForSubscriptionRestriction(tokenRestrictionResult)) {
                closeAuthorizationPopup(authWindow);
                await options?.onRestriction?.(tokenRestrictionResult);
                return tokenRestrictionResult;
            }
            if (tokenBody.blockConnect) {
                closeAuthorizationPopup(authWindow);
                return tokenRestrictionResult;
            }

            const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
            if (!nangoToken) {
                closeAuthorizationPopup(authWindow);
                throw new Error('Failed to retrieve connect token');
            }

            const nango = new Nango({ connectSessionToken: nangoToken });
            let result;
            try {
                result = await runNangoAuthorizationWithWorkspaceRecovery({
                    authWindow,
                    nango,
                    provider: verificationProvider,
                    startAuth: () => nango.auth(providerConfigKey),
                });
            } catch (error) {
                closeAuthorizationPopup(authWindow);
                const restrictionResult = buildConnectRestrictionResultFromError(error);
                if (restrictionResult) {
                    return restrictionResult;
                }
                throw error;
            }

            if (result?.recoveredAfterAuthResultMissing) {
                nango.clear();
                closeAuthorizationPopup(authWindow);
            }

            const verificationResult = await verifyNangoConnection(
                verificationProvider,
                result.connectionId
            );
            return verificationResult || buildNangoConnectResult(tokenBody, result, true);
        };

        const connectNangoCredentials = async ({
            integrationId,
            providerConfigKey,
            verificationProvider,
            authOptions,
            accountDomain,
        }: {
            integrationId: string;
            providerConfigKey: string;
            verificationProvider: string;
            authOptions: AuthOptions;
            accountDomain?: string;
        }) => {
            let tokenBody;
            try {
                tokenBody = await authService.getNangoConnectToken({
                    integration_id: integrationId,
                    provider_config_key: providerConfigKey,
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
            } catch (error) {
                const restrictionResult = buildConnectRestrictionResultFromError(error);
                if (restrictionResult) {
                    return restrictionResult;
                }
                throw error;
            }
            if (tokenBody.blockConnect) {
                return buildNangoConnectResult(tokenBody);
            }

            const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
            if (!nangoToken) {
                throw new Error('Failed to retrieve connect token');
            }

            const nango = new Nango({ connectSessionToken: nangoToken });
            let result;
            try {
                result = await nango.auth(providerConfigKey, authOptions);
            } catch (error) {
                const restrictionResult = buildConnectRestrictionResultFromError(error);
                if (restrictionResult) {
                    return restrictionResult;
                }
                throw error;
            }
            const verificationResult = await verifyNangoConnection(
                verificationProvider,
                result.connectionId
            );
            if (verificationResult) {
                return verificationResult;
            }

            await updateConnectedIntegrationLabel(
                verificationProvider,
                connectionConfig,
                { connectionId: result.connectionId, shopDomain: accountDomain }
            ).catch((labelError) => {
                console.warn(`Failed to update ${verificationProvider} integration label after connect:`, labelError);
            });
            return buildNangoConnectResult(tokenBody, result, true);
        };

        const connectBustlyHosted = async (
            integrationKey: string,
            verificationProvider: string,
            requiresAccountSelection: boolean,
        ) => {
            const authWindow = openAuthorizationPopup(
                'Unable to open the Bustly authorization window. Please allow pop-ups and try again.'
            );
            let session;
            try {
                session = await authService.createBustlyConnectSession({
                    integrationKey,
                    connectionScene: resolveConnectionScene(connectionConfig),
                    onboardingFlow: connectionConfig?.onboarding_flow,
                    redirectUri: `${window.location.origin}/admin/integrations/bustly/callback`,
                    axiosRequestConfig,
                });
            } catch (error) {
                closeAuthorizationPopup(authWindow);
                throw error;
            }

            const resultPromise = waitForBustlyHostedConnectPopupResult(
                authWindow,
                window.location.origin,
                session.handoff,
                session.flowId
            );
            persistBustlyHostedConnectHandoff(authWindow, session.handoff);
            authWindow.location.replace(session.connectUrl);
            try { authWindow.focus(); } catch {}
            await options?.onAuthorizationStarted?.();

            let hostedResult;
            try {
                hostedResult = await resultPromise;
            } finally {
                closeAuthorizationPopup(authWindow);
            }
            if (hostedResult.status === 'error') {
                throw new Error(hostedResult.error || 'Bustly authorization failed');
            }
            if (!hostedResult.connectionId) {
                throw new Error('Bustly authorization finished without a connection ID');
            }

            const registrationAttempts = hostedResult.status === 'pending' ? 45 : 1;
            let registrationError: unknown;
            for (let attempt = 0; attempt < registrationAttempts; attempt++) {
                try {
                    await authService.registerBustlyIntegration({
                        externalConnectionId: hostedResult.connectionId,
                        integrationKey,
                        label: resolveConnectionLabel(connectionConfig) || undefined,
                        axiosRequestConfig,
                    });
                    registrationError = null;
                    break;
                } catch (error) {
                    registrationError = error;
                    if (attempt < registrationAttempts - 1) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                }
            }
            if (registrationError) {
                throw registrationError;
            }

            for (let attempt = 0; attempt < 8; attempt++) {
                const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                if (isIntegrationVerificationSatisfied(
                    refreshedIntegrations,
                    verificationProvider,
                    hostedResult.connectionId,
                    buildConnectVerificationOptions(verificationProvider)
                )) {
                    return {
                        success: true,
                        connectionId: hostedResult.connectionId,
                        connectionBackend: 'BUSTLY' as const,
                        requiresAccountSelection,
                    };
                }
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            throw new Error(`Failed to register ${verificationProvider} integration`);
        };

        try {
            const hostedDispatch = await dispatchBustlyHostedConnect(
                normalizedProvider,
                (integrationKey, requiresAccountSelection) => connectBustlyHosted(
                    integrationKey,
                    normalizedProvider,
                    requiresAccountSelection,
                ),
            );
            if (hostedDispatch.handled) {
                return hostedDispatch.result;
            }

            switch (normalizedProvider) {
            case 'shopify': {
                // Shopify 连接
                const { shop_domain, redirect_url } = connectionConfig || {};
                if (!shop_domain) {
                    throw new Error('shop_domain is required for Shopify connection');
                }

                const authWindow = openAuthorizationPopup('Unable to open the Shopify authorization window. Please allow pop-ups for Bustly and try again.');

                let res;
                try {
                    res = await authService.connectShopify({
                        shop_domain,
                        redirect_url: redirect_url,
                        connection_alias: onboardingConnectionBaseline
                            ? resolveConnectionLabel(connectionConfig) || undefined
                            : undefined,
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig,
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restriction = resolveRestrictionFromConnectError(error);
                    if (restriction) {
                        return { success: false, ...restriction };
                    }
                    throw error;
                }

                if (res.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: res.restrictionState ?? res.eligibilityState,
                        blockConnect: res.blockConnect,
                        upgradeRequired: res.upgradeRequired ?? false,
                        ctaAction: res.ctaAction,
                        eligibilityState: res.eligibilityState,
                        allowsFreshTrial: res.allowsFreshTrial,
                        eligibilityMessage: res.eligibilityMessage,
                        message: res.message,
                        trialRemainingDays: res.trialRemainingDays,
                        currentWorkspaceId: res.currentWorkspaceId,
                        currentWorkspaceName: res.currentWorkspaceName,
                        currentStoreCount: res.currentStoreCount,
                        storeLimit: res.storeLimit,
                        upgradeTarget: res.upgradeTarget,
                    };
                }

                if (res.authorizationUrl) {
                    authWindow.location.replace(res.authorizationUrl);
                    await options?.onAuthorizationStarted?.();
                    try { authWindow.focus(); } catch {}

                    // The OAuth return normally notifies this window through BroadcastChannel.
                    // Treat the persisted integration as an equivalent success signal: some
                    // browsers can complete the return without delivering that message.
                    let callbackRestrictionPayload: ConnectRestrictionPayload | null = null;
                    let shopifyCallbackReceived = false;
                    let shopifyCallbackChannel: BroadcastChannel | null = null;
                    let shopifyAuthorizationTimeout: ReturnType<typeof setTimeout> | null = null;
                    const shopifyVerificationOptions = {
                        // A Workspace may already have other Shopify stores. The OAuth
                        // callback and polling fallback must verify this submitted store,
                        // never treat an existing provider-level connection as success.
                        expectedAccount: shop_domain,
                    };
                    const stopWaitingForShopifyAuthorization = () => {
                        if (shopifyAuthorizationTimeout) {
                            clearTimeout(shopifyAuthorizationTimeout);
                            shopifyAuthorizationTimeout = null;
                        }
                        shopifyCallbackChannel?.close();
                        shopifyCallbackChannel = null;
                    };

                    const waitForShopifyIntegrationRecord = async (maxAttempts = 150) => {
                        for (let attempt = 0; attempt < maxAttempts; attempt++) {
                            await new Promise(resolve => setTimeout(resolve, 2000));
                            const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                            if (isIntegrationVerificationSatisfied(
                                refreshedIntegrations,
                                'shopify',
                                undefined,
                                shopifyVerificationOptions,
                            )) {
                                return;
                            }
                        }

                        throw new Error('Shopify authorization timeout');
                    };

                    const waitForShopifyPopupCloseRecovery = async () => {
                        while (!shopifyCallbackReceived) {
                            await new Promise(resolve => setTimeout(resolve, 500));
                            if (authWindow.closed) {
                                return 'popup-closed' as const;
                            }
                        }

                        await new Promise<void>(() => {});
                    };

                    try {
                        const shopifyCallbackPromise = new Promise<void>((resolve, reject) => {
                            shopifyCallbackChannel = new BroadcastChannel('connect');
                            shopifyAuthorizationTimeout = setTimeout(() => {
                                reject(new Error('Shopify authorization timeout'));
                            }, 300000); // 5 分钟超时

                            shopifyCallbackChannel.onmessage = async (e) => {
                                shopifyCallbackReceived = true;
                                if (e.data.type === 'SUCCESS') {
                                    try {
                                        console.log('connect 成功', e.data);
                                        callbackRestrictionPayload = isConnectRestrictionPayload(e.data.payload)
                                            ? e.data.payload
                                            : null;
                                        
                                        if (!callbackRestrictionPayload?.blockConnect) {
                                            // 验证集成是否创建成功
                                            await checkIntegrationCreatedForConnect(
                                                'shopify',
                                                undefined,
                                                shopifyVerificationOptions,
                                            );
                                        }
                                        resolve();
                                    } catch (connectError) {
                                        reject(connectError instanceof Error ? connectError : new Error('Shopify authorization failed'));
                                    }
                                } else if (e.data.type === 'ERROR') {
                                    reject(new Error(e.data.message || 'Shopify authorization failed'));
                                }
                            };
                        });

                        const shopifyIntegrationRecordPromise = waitForShopifyIntegrationRecord();
                        const shopifyAuthorizationSignal = await Promise.race([
                            shopifyCallbackPromise.then(() => 'callback' as const),
                            shopifyIntegrationRecordPromise.then(() => 'integration-record' as const),
                            waitForShopifyPopupCloseRecovery(),
                        ]);
                        if (shopifyAuthorizationSignal === 'popup-closed') {
                            try {
                                await shopifyIntegrationRecordPromise;
                            } catch {
                                throw new Error('Shopify authorization finished, but no connection was created. Please try again.');
                            }
                        }
                        if (!((callbackRestrictionPayload as ConnectRestrictionPayload | null)?.blockConnect)) {
                            await updateConnectedIntegrationLabel('shopify', connectionConfig, { shopDomain: shop_domain }).catch((labelError) => {
                                console.warn('Failed to update Shopify integration label after connect:', labelError);
                            });
                        }
                    } catch (error) {
                        closeAuthorizationPopup(authWindow);
                        const restriction = resolveRestrictionFromConnectError(error);
                        if (restriction) {
                            return { success: false, ...restriction };
                        }
                        throw error;
                    } finally {
                        stopWaitingForShopifyAuthorization();
                    }

                    const finalRestrictionPayload = callbackRestrictionPayload
                        ? (() => {
                            const restrictionPayload = callbackRestrictionPayload as ConnectRestrictionPayload;
                            return {
                                restrictionState: restrictionPayload.restrictionState ?? restrictionPayload.eligibilityState ?? res.restrictionState ?? res.eligibilityState,
                                blockConnect: restrictionPayload.blockConnect ?? res.blockConnect,
                                upgradeRequired: restrictionPayload.upgradeRequired ?? res.upgradeRequired ?? false,
                                ctaAction: restrictionPayload.ctaAction ?? res.ctaAction,
                                eligibilityState: restrictionPayload.eligibilityState ?? res.eligibilityState,
                                allowsFreshTrial: restrictionPayload.allowsFreshTrial ?? res.allowsFreshTrial,
                                eligibilityMessage: restrictionPayload.eligibilityMessage ?? res.eligibilityMessage,
                                message: restrictionPayload.message ?? res.message ?? res.eligibilityMessage,
                                trialRemainingDays: restrictionPayload.trialRemainingDays ?? res.trialRemainingDays,
                                currentWorkspaceId: restrictionPayload.currentWorkspaceId ?? res.currentWorkspaceId,
                                currentWorkspaceName: restrictionPayload.currentWorkspaceName ?? res.currentWorkspaceName,
                                currentStoreCount: restrictionPayload.currentStoreCount ?? res.currentStoreCount,
                                storeLimit: restrictionPayload.storeLimit ?? res.storeLimit,
                                upgradeTarget: restrictionPayload.upgradeTarget ?? res.upgradeTarget,
                            };
                        })()
                        : res;

                    return {
                        success: !finalRestrictionPayload.blockConnect,
                        restrictionState: finalRestrictionPayload.restrictionState ?? finalRestrictionPayload.eligibilityState,
                        blockConnect: finalRestrictionPayload.blockConnect,
                        upgradeRequired: finalRestrictionPayload.upgradeRequired ?? false,
                        ctaAction: finalRestrictionPayload.ctaAction,
                        eligibilityState: finalRestrictionPayload.eligibilityState,
                        allowsFreshTrial: finalRestrictionPayload.allowsFreshTrial,
                        eligibilityMessage: finalRestrictionPayload.eligibilityMessage,
                        message: finalRestrictionPayload.message,
                        trialRemainingDays: finalRestrictionPayload.trialRemainingDays,
                        currentWorkspaceId: finalRestrictionPayload.currentWorkspaceId,
                        currentWorkspaceName: finalRestrictionPayload.currentWorkspaceName,
                        currentStoreCount: finalRestrictionPayload.currentStoreCount,
                        storeLimit: finalRestrictionPayload.storeLimit,
                        upgradeTarget: finalRestrictionPayload.upgradeTarget,
                    };
                } else {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Shopify store is not found. Please check the store url.');
                }
            }

            case 'wix': {
                // Wix 连接：支持两种模式
                // 1) redirect_callback（旧模式）通过 BroadcastChannel 回调
                // 2) webhook_polling（新模式）安装后轮询 discover 并完成绑定
                const authWindow = openAuthorizationPopup('Failed to open Wix authorization window. Please allow popups for this site.');

                const redirectUrl = (() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('connect_success', 'wix');
                    url.searchParams.set('workspace_id', workspaceId);
                    url.searchParams.set('type', 'wix');
                    return url.toString();
                })();

                let res;
                try {
                    res = await authService.initWixConnect({
                        workspace_id: workspaceId,
                        redirect_url: redirectUrl,
                        axiosRequestConfig
                    });
                } catch (error) {
                    authWindow.close();
                    throw error;
                }

                if (res.blockConnect) {
                    authWindow.close();
                    return {
                        success: false,
                        restrictionState: res.restrictionState ?? res.eligibilityState,
                        blockConnect: res.blockConnect,
                        upgradeRequired: res.upgradeRequired ?? false,
                        ctaAction: res.ctaAction,
                        eligibilityState: res.eligibilityState,
                        allowsFreshTrial: res.allowsFreshTrial,
                        eligibilityMessage: res.eligibilityMessage,
                        message: res.message ?? res.eligibilityMessage,
                        trialRemainingDays: res.trialRemainingDays,
                        currentWorkspaceId: res.currentWorkspaceId,
                        currentWorkspaceName: res.currentWorkspaceName,
                        currentStoreCount: res.currentStoreCount,
                        storeLimit: res.storeLimit,
                        upgradeTarget: res.upgradeTarget,
                    };
                }

                if (!res.install_url) {
                    authWindow.close();
                    throw new Error('Failed to get Wix install URL');
                }

                authWindow.location.href = res.install_url;

                if (res.mode === 'redirect_callback') {
                    await new Promise<void>((resolve, reject) => {
                        const channel = new BroadcastChannel('connect');
                        const timeout = setTimeout(() => {
                            channel.close();
                            reject(new Error('Wix authorization timeout'));
                        }, 300000); // 5 分钟超时

                        channel.onmessage = async (e) => {
                            if (e.data.type === 'SUCCESS' && e.data.provider === 'wix') {
                                clearTimeout(timeout);
                                channel.close();
                                await checkIntegrationCreatedForConnect('wix');
                                resolve();
                            } else if (e.data.type === 'ERROR' && e.data.provider === 'wix') {
                                clearTimeout(timeout);
                                channel.close();
                                reject(new Error(e.data.message || 'Wix authorization failed'));
                            }
                        };
                    });

                    return { success: true };
                }

                const startedAt = Date.now();
                const timeoutMs = 2 * 60 * 1000;
                const connectStartedAtIso = new Date(startedAt - 60 * 1000).toISOString();
                let discoveredInstanceId: string | null = null;
                let lastDiscoveryError: Error | null = null;
                let triedFallbackWithoutTimeFilter = false;

                while (Date.now() - startedAt < timeoutMs) {
                    const elapsedMs = Date.now() - startedAt;
                    await new Promise(resolve => setTimeout(resolve, 2000));

                    try {
                        const discovery = await authService.discoverWixConnect({
                            workspace_id: workspaceId,
                            not_before: connectStartedAtIso,
                            axiosRequestConfig
                        });

                        if (discovery.instance_id) {
                            discoveredInstanceId = discovery.instance_id;
                            break;
                        }

                        if (discovery.source === 'all_claimed') {
                            throw new Error('This Wix site is already connected to another workspace. Switch to that workspace or disconnect it there first.');
                        }

                        // 30 秒后允许降级：不再要求 webhook 必须是“刚刚新写入”的记录，
                        // 兼容“应用早已安装、未触发新 webhook”场景。
                        if (!triedFallbackWithoutTimeFilter && elapsedMs >= 30 * 1000) {
                            triedFallbackWithoutTimeFilter = true;
                            const fallbackDiscovery = await authService.discoverWixConnect({
                                workspace_id: workspaceId,
                                axiosRequestConfig
                            });
                            if (fallbackDiscovery.instance_id) {
                                discoveredInstanceId = fallbackDiscovery.instance_id;
                                break;
                            }
                            if (fallbackDiscovery.source === 'all_claimed') {
                                throw new Error('This Wix site is already connected to another workspace. Switch to that workspace or disconnect it there first.');
                            }
                        }
                    } catch (error: any) {
                        lastDiscoveryError = error instanceof Error ? error : new Error(String(error));
                    }

                    // 用户手动关窗且 15 秒内仍无实例，提前结束，避免长时间转圈
                    if (authWindow.closed && elapsedMs >= 15 * 1000 && !discoveredInstanceId) {
                        try {
                            const fallbackDiscovery = await authService.discoverWixConnect({
                                workspace_id: workspaceId,
                                axiosRequestConfig
                            });
                            if (fallbackDiscovery.instance_id) {
                                discoveredInstanceId = fallbackDiscovery.instance_id;
                                break;
                            }
                        } catch (error: any) {
                            lastDiscoveryError = error instanceof Error ? error : new Error(String(error));
                        }
                        throw new Error('Wix authorization window was closed before installation data was discovered.');
                    }
                }

                if (!discoveredInstanceId) {
                    throw new Error(lastDiscoveryError?.message || 'Wix installation detected no instance_id. Please confirm App Instance Installed webhook is configured and retry.');
                }

                await authService.completeWixConnect({
                    workspace_id: workspaceId,
                    callback_params: {
                        instanceId: discoveredInstanceId
                    },
                    callback_url: window.location.href,
                    axiosRequestConfig
                });

                await checkIntegrationCreatedForConnect(
                    'wix',
                    undefined,
                    {
                        expectedAccount: onboardingConnectionBaseline
                            ? discoveredInstanceId
                            : undefined,
                    }
                );
                if (!authWindow.closed) {
                    authWindow.close();
                }

                return { success: true };
            }

            case 'google-ads': {
                // Google Ads 连接
                const authWindow = openAuthorizationPopup('Unable to open the Google Ads authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'google-ads',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        ctaAction: tokenBody.ctaAction,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
                const requiresAccountSelection = Boolean(
                    tokenBody.requiresAccountSelection
                    || tokenBody.data?.requiresAccountSelection
                    || tokenBody.result?.requiresAccountSelection
                );

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                let stopGoogleAdsRecovery = false;
                const googleAdsAuthStartedAt = new Date().toISOString();
                try {
                    const authResultPromise = runWithPreopenedNangoPopup(
                        authWindow,
                        () => nango.auth('google-ads')
                    );
                    const recoveredConnectionPromise = waitForGoogleAdsConnectionCreatedAfter(
                        googleAdsAuthStartedAt,
                        {
                            maxAttempts: 300,
                            shouldStop: () => stopGoogleAdsRecovery,
                        }
                    );

                    result = await Promise.race([
                        authResultPromise,
                        recoveredConnectionPromise.then((connectionId) => ({
                            connectionId,
                            recoveredAfterAuthResultMissing: true,
                        })),
                    ]);

                    stopGoogleAdsRecovery = true;
                    if (
                        result
                        && 'recoveredAfterAuthResultMissing' in result
                        && result.recoveredAfterAuthResultMissing
                    ) {
                        nango.clear();
                        closeAuthorizationPopup(authWindow);
                    }
                } catch (error) {
                    stopGoogleAdsRecovery = true;
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                // Only proceed if we have a connection ID
                if (result?.connectionId) {
                    const verificationResult = await verifyNangoConnection('google-ads', result.connectionId);
                    if (verificationResult) {
                        return verificationResult;
                    }

                    return requiresAccountSelection
                        ? buildNangoConnectResult(tokenBody, { ...result, requiresAccountSelection: true }, true)
                        : buildNangoConnectResult(tokenBody, result, true);
                }

                return buildNangoConnectResult(tokenBody, result, true);
            }

            case 'google-analytics': {
                // Google Analytics 连接
                const authWindow = openAuthorizationPopup('Unable to open the Google Analytics authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'google-analytics',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return buildNangoConnectResult(tokenBody);
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                try {
                    result = await runNangoAuthorizationWithWorkspaceRecovery({
                        authWindow,
                        nango,
                        provider: 'google-analytics',
                        startAuth: () => nango.auth('google-analytics'),
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }

                if (result?.recoveredAfterAuthResultMissing) {
                    nango.clear();
                    closeAuthorizationPopup(authWindow);
                }
                if (result?.connectionId) {
                    const verificationResult = await verifyNangoConnection('google-analytics', result.connectionId);
                    if (verificationResult) {
                        return verificationResult;
                    }
                }

                return buildNangoConnectResult(tokenBody, result, true);
            }

            case 'tiktok-ads': {
                const authWindow = openAuthorizationPopup('Unable to open the TikTok Ads authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'tiktok-ads',
                        provider_config_key: 'tiktok-ads',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return buildNangoConnectResult(tokenBody);
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                try {
                    result = await runNangoAuthorizationWithWorkspaceRecovery({
                        authWindow,
                        nango,
                        provider: 'tiktok-ads',
                        startAuth: () => nango.auth('tiktok-ads'),
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }

                if (result?.recoveredAfterAuthResultMissing) {
                    nango.clear();
                    closeAuthorizationPopup(authWindow);
                }
                if (result?.connectionId) {
                    const verificationResult = await verifyNangoConnection('tiktok-ads', result.connectionId);
                    if (verificationResult) {
                        return verificationResult;
                    }
                }

                return buildNangoConnectResult(tokenBody, result, true);
            }

            case 'google-search-console': {
                const authWindow = openAuthorizationPopup('Unable to open the Google Search Console authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'google-search-console',
                        provider_config_key: 'google-search-console',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return buildNangoConnectResult(tokenBody);
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                try {
                    result = await runNangoAuthorizationWithWorkspaceRecovery({
                        authWindow,
                        nango,
                        provider: 'google-search-console',
                        startAuth: () => nango.auth('google-search-console'),
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }

                if (result?.recoveredAfterAuthResultMissing) {
                    nango.clear();
                    closeAuthorizationPopup(authWindow);
                }
                if (result?.connectionId) {
                    const verificationResult = await verifyNangoConnection('google-search-console', result.connectionId);
                    if (verificationResult) {
                        return verificationResult;
                    }
                }

                return buildNangoConnectResult(tokenBody, result, true);
            }

            case 'semrush': {
                const apiKey = String(connectionConfig?.apiKey || '').trim();
                if (!apiKey) {
                    throw new Error('Semrush API key is required');
                }

                return connectNangoCredentials({
                    integrationId: 'semrush',
                    providerConfigKey: 'semrush',
                    verificationProvider: 'semrush',
                    authOptions: {
                        credentials: { apiKey },
                    },
                });
            }

            case 'figma-oauth': {
                return connectNangoOAuthDirectly({
                    integrationId: 'figma',
                    providerConfigKey: 'figma',
                    verificationProvider: 'figma',
                    displayName: 'Figma',
                });
            }

            case 'judgeme':
            case 'judge.me':
            case 'judge-me': {
                const oauthPlatform = 'judgeme';
                const displayName = 'Judge.me';
                const redirectUri = `${window.location.origin}${window.location.pathname}`;
                const flowId = createNativeOAuthFlowId();
                const returnUri = buildJudgeMeOAuthReturnUri(flowId);
                const popup = openAuthorizationPopup(`Failed to open ${displayName} authorization window. Please allow popups for this site.`);

                let connectInit;
                try {
                    connectInit = await authService.initJudgeMeOAuth({
                        workspace_id: workspaceId,
                        platform: oauthPlatform,
                        redirect_uri: redirectUri,
                        return_uri: returnUri,
                        connection_name: connectionConfig?.connection_name || connectionConfig?.connectionName,
                        connection_alias: connectionConfig?.connection_alias || connectionConfig?.connectionAlias || connectionConfig?.label,
                        ...buildSceneRequest(connectionConfig),
                    });
                } catch (error) {
                    closeAuthorizationPopup(popup);
                    throw error;
                }

                if (!connectInit.authorize_url) {
                    closeAuthorizationPopup(popup);
                    throw new Error(`Failed to initialize ${displayName} authorization URL`);
                }

                const popupResult = waitForNativeOAuthPopupResult(popup, displayName, undefined, {
                    expectedFlowId: flowId,
                    expectedState: connectInit.state,
                });
                popup.location.replace(buildJudgeMeLoginAuthorizeUrl(connectInit.authorize_url));
                try { popup.focus(); } catch {}

                let oauthResult: NativeOAuthPopupResult;
                try {
                    oauthResult = await popupResult;
                } catch (error) {
                    const message = getErrorMessage(error) || '';
                    if (!/authorization window was closed|window was closed/i.test(message)) {
                        throw error;
                    }
                    await checkIntegrationCreatedForConnect(oauthPlatform);
                    return { success: true };
                }
                let connectionId = oauthResult.connection || undefined;
                if (!oauthResult.connected) {
                    if (!oauthResult.code || !oauthResult.state) {
                        throw new Error(`${displayName} authorization did not return an OAuth code`);
                    }
                    const completed = await authService.completeJudgeMeOAuth({
                        workspace_id: workspaceId,
                        platform: oauthPlatform,
                        code: oauthResult.code,
                        state: oauthResult.state,
                        redirect_uri: connectInit.redirect_uri,
                    });
                    connectionId = completed.connection || connectionId;
                }
                await checkIntegrationCreatedForConnect(oauthPlatform, connectionId);
                return { success: true };
            }

            case 'slack': {
                const fallbackRedirectUri = (() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('connect_success', 'slack');
                    url.searchParams.set('workspace_id', workspaceId);
                    url.searchParams.set('type', 'slack');
                    return url.toString();
                })();

                const popup = openAuthorizationPopup('Failed to open Slack authorization window. Please allow popups for this site.');

                let connectInit;
                try {
                    connectInit = await authService.initSlackConnect({
                        workspace_id: workspaceId,
                        redirect_uri: connectionConfig?.redirect_uri,
                        return_url: connectionConfig?.return_url ?? fallbackRedirectUri
                    });
                } catch (error) {
                    closeAuthorizationPopup(popup);
                    throw error;
                }

                if (!connectInit.authorize_url) {
                    closeAuthorizationPopup(popup);
                    throw new Error('Failed to initialize Slack authorization URL');
                }

                const popupResult = waitForSlackOAuthPopupResult(popup);
                popup.location.replace(connectInit.authorize_url);
                try { popup.focus(); } catch {}

                const oauthResult = await popupResult;

                await authService.completeSlackConnect({
                    code: oauthResult.code,
                    state: oauthResult.state,
                    redirect_uri: connectInit.redirect_uri
                });

                await checkIntegrationCreatedForConnect('slack');
                return { success: true };
            }

            case 'bigcommerce': {
                // BigCommerce 连接
                const requestBody = {
                    integration_id: 'bigcommerce',
                    ...connectionConfig,
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                };

                const authWindow = openAuthorizationPopup('Unable to open the BigCommerce authorization window. Please allow pop-ups for Bustly and try again.');

                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken(requestBody);
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restrictionResult = buildConnectRestrictionResultFromError(error);
                    if (restrictionResult) {
                        return restrictionResult;
                    }
                    throw error;
                }

                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        ctaAction: tokenBody.ctaAction,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        message: tokenBody.message,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }

                // 处理重定向（App 安装）
                if (tokenBody.action === 'REDIRECT' && tokenBody.url) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        action: 'REDIRECT',
                        url: tokenBody.url,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        ctaAction: tokenBody.ctaAction,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        message: tokenBody.message,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }

                // 处理 Token 响应（连接）
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('未获取到 Token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });

                if (!connectionConfig?.store_hash || !connectionConfig?.accountUuid) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('BigCommerce requires store hash and account UUID');
                }

                const authParams = {
                    params: {
                        storeHash: connectionConfig.store_hash,
                        accountUuid: connectionConfig.accountUuid
                    }
                };

                try {
                    await options?.onAuthorizationStarted?.();
                    // @ts-ignore
                    const result = await runWithPreopenedNangoPopup(authWindow, () => nango.auth('bigcommerce', undefined, authParams as any));

                    if (result?.connectionId) {
                        // 验证集成是否创建成功
                        await checkIntegrationCreatedForConnect('bigcommerce', result.connectionId);
                        await updateConnectedIntegrationLabel('bigcommerce', connectionConfig, { connectionId: result.connectionId }).catch((labelError) => {
                            console.warn('Failed to update BigCommerce integration label after connect:', labelError);
                        });
                    }

                    return {
                        ...result,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        ctaAction: tokenBody.ctaAction,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        message: tokenBody.message,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restriction = resolveRestrictionFromConnectError(error);
                    if (restriction) {
                        return { success: false, ...restriction };
                    }
                    throw error;
                }
            }

            case 'woocommerce': {
                const storeURL = String(connectionConfig?.storeURL || '').trim();
                const consumerKey = String(connectionConfig?.consumerKey || '').trim();
                const consumerSecret = String(connectionConfig?.consumerSecret || '').trim();
                if (!storeURL || !consumerKey || !consumerSecret) {
                    throw new Error('WooCommerce store URL, Consumer Key, and Consumer Secret are required');
                }

                return connectNangoCredentials({
                    integrationId: 'woocommerce',
                    providerConfigKey: 'woocommerce',
                    verificationProvider: 'woocommerce',
                    authOptions: {
                        params: { storeURL },
                        credentials: {
                            username: consumerKey,
                            password: consumerSecret,
                        },
                    },
                    accountDomain: storeURL,
                });
            }

            case 'magento':
            case 'adobe-commerce': {
                const storeDomain = String(connectionConfig?.storeDomain || '').trim();
                const storeCode = String(connectionConfig?.storeCode || '').trim();
                const accessToken = String(connectionConfig?.accessToken || '').trim();
                if (!storeDomain || !storeCode || !accessToken) {
                    throw new Error('Adobe Commerce Store Domain, Store Code, and Access Token are required');
                }

                return connectNangoCredentials({
                    integrationId: 'adobe-commerce',
                    providerConfigKey: 'adobe-commerce',
                    verificationProvider: 'adobe-commerce',
                    authOptions: {
                        params: { storeDomain, storeCode },
                        credentials: { apiKey: accessToken },
                    },
                    accountDomain: storeDomain,
                });
            }

            case 'aliexpress': {
                // Aliexpress 连接 - 获取授权 URL 并打开新窗口
                const authWindow = openAuthorizationPopup('Failed to open authorization window. Please allow popups for this site.');
                let res;
                try {
                    res = await authService.authorizeAliexpress({
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (res.auth_url) {
                    authWindow.location.replace(res.auth_url);
                    try { authWindow.focus(); } catch {}

                    // 等待窗口关闭，然后检查连接状态
                    await new Promise<void>((resolve, reject) => {
                        const checkInterval = setInterval(async () => {
                            if (authWindow.closed) {
                                clearInterval(checkInterval);
                                // 窗口关闭后，检查一次连接状态
                                const integrations = await fetchIntegrations(false, { forceRefresh: true });
                                const aliexpressIntegration = integrations.find(i => i.provider === 'aliexpress');

                                if (aliexpressIntegration?.connected) {
                                    // 授权成功
                                    resolve();
                                } else {
                                    // 用户取消授权
                                    reject(new Error('Aliexpress authorization window was closed'));
                                }
                            }
                        }, 500);
                    });

                    return { success: true };
                } else {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to get Aliexpress authorization URL');
                }
            }

            case 'mailchimp':
            case 'mailchimp-oauth': {
                return connectNangoOAuthDirectly({
                    integrationId: 'mailchimp',
                    providerConfigKey: 'mailchimp',
                    verificationProvider: 'mailchimp',
                    displayName: 'Mailchimp',
                });
            }

            case 'klaviyo': {
                return connectNangoOAuthDirectly({
                    integrationId: 'klaviyo-oauth',
                    providerConfigKey: 'klaviyo-oauth',
                    verificationProvider: 'klaviyo',
                    displayName: 'Klaviyo',
                });
            }

            case 'feishu': {
                const fallbackRedirectUri = (() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('connect_success', 'feishu');
                    url.searchParams.set('workspace_id', workspaceId);
                    url.searchParams.set('type', 'feishu');
                    return url.toString();
                })();

                const authWindow = openAuthorizationPopup('Failed to open Feishu authorization window. Please allow popups for this site.');

                let connectInit;
                try {
                    connectInit = await authService.initFeishuConnect({
                        workspace_id: workspaceId,
                        region: connectionConfig?.region,
                        provider: connectionConfig?.provider,
                        app_id: connectionConfig?.app_id,
                        client_id: connectionConfig?.client_id,
                        app_secret: connectionConfig?.app_secret,
                        client_secret: connectionConfig?.client_secret,
                        scope: connectionConfig?.scope,
                        redirect_uri: connectionConfig?.redirect_uri,
                        return_url: connectionConfig?.return_url ?? fallbackRedirectUri,
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (connectInit.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: connectInit.restrictionState ?? connectInit.eligibilityState,
                        blockConnect: connectInit.blockConnect,
                        upgradeRequired: connectInit.upgradeRequired ?? false,
                        ctaAction: connectInit.ctaAction,
                        eligibilityState: connectInit.eligibilityState,
                        allowsFreshTrial: connectInit.allowsFreshTrial,
                        message: connectInit.message ?? connectInit.eligibilityMessage,
                        eligibilityMessage: connectInit.eligibilityMessage,
                        trialRemainingDays: connectInit.trialRemainingDays,
                        currentWorkspaceId: connectInit.currentWorkspaceId,
                        currentWorkspaceName: connectInit.currentWorkspaceName,
                    };
                }

                if (!connectInit.authorize_url) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error(connectInit.message || 'Failed to initialize Feishu authorization URL');
                }

                authWindow.location.replace(connectInit.authorize_url);
                await options?.onAuthorizationStarted?.();
                try { authWindow.focus(); } catch {}

                try {
                    const popupResult = await waitForFeishuOAuthPopupResult(authWindow);
                    await checkIntegrationCreatedForConnect('feishu', popupResult.connection || undefined);
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                closeAuthorizationPopup(authWindow);

                return {
                    success: true,
                    restrictionState: connectInit.restrictionState ?? connectInit.eligibilityState,
                    blockConnect: connectInit.blockConnect,
                    upgradeRequired: connectInit.upgradeRequired ?? false,
                    ctaAction: connectInit.ctaAction,
                    eligibilityState: connectInit.eligibilityState,
                    allowsFreshTrial: connectInit.allowsFreshTrial,
                    message: connectInit.message ?? connectInit.eligibilityMessage,
                    eligibilityMessage: connectInit.eligibilityMessage,
                    trialRemainingDays: connectInit.trialRemainingDays,
                    currentWorkspaceId: connectInit.currentWorkspaceId,
                    currentWorkspaceName: connectInit.currentWorkspaceName,
                };
            }

            case 'dingtalk': {
                const fallbackRedirectUri = (() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('connect_success', 'dingtalk');
                    url.searchParams.set('workspace_id', workspaceId);
                    url.searchParams.set('type', 'dingtalk');
                    return url.toString();
                })();

                const authWindow = openAuthorizationPopup('Failed to open DingTalk authorization window. Please allow popups for this site.');

                let connectInit;
                try {
                    connectInit = await authService.initDingTalkConnect({
                        workspace_id: workspaceId,
                        region: connectionConfig?.region,
                        provider: connectionConfig?.provider,
                        app_id: connectionConfig?.app_id,
                        client_id: connectionConfig?.client_id,
                        app_secret: connectionConfig?.app_secret,
                        client_secret: connectionConfig?.client_secret,
                        scope: connectionConfig?.scope,
                        redirect_uri: connectionConfig?.redirect_uri,
                        return_url: connectionConfig?.return_url ?? fallbackRedirectUri,
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (connectInit.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: connectInit.restrictionState ?? connectInit.eligibilityState,
                        blockConnect: connectInit.blockConnect,
                        upgradeRequired: connectInit.upgradeRequired ?? false,
                        eligibilityState: connectInit.eligibilityState,
                        allowsFreshTrial: connectInit.allowsFreshTrial,
                        message: connectInit.message ?? connectInit.eligibilityMessage,
                        eligibilityMessage: connectInit.eligibilityMessage,
                        trialRemainingDays: connectInit.trialRemainingDays,
                        currentWorkspaceId: connectInit.currentWorkspaceId,
                        currentWorkspaceName: connectInit.currentWorkspaceName,
                    };
                }

                if (!connectInit.authorize_url) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error(connectInit.message || 'Failed to initialize DingTalk authorization URL');
                }

                authWindow.location.replace(connectInit.authorize_url);
                await options?.onAuthorizationStarted?.();
                try { authWindow.focus(); } catch {}

                try {
                    const popupResult = await waitForDingTalkOAuthPopupResult(authWindow);
                    await checkIntegrationCreatedForConnect('dingtalk', popupResult.connection || undefined);
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                closeAuthorizationPopup(authWindow);

                return {
                    success: true,
                    restrictionState: connectInit.restrictionState ?? connectInit.eligibilityState,
                    blockConnect: connectInit.blockConnect,
                    upgradeRequired: connectInit.upgradeRequired ?? false,
                    eligibilityState: connectInit.eligibilityState,
                    allowsFreshTrial: connectInit.allowsFreshTrial,
                    message: connectInit.message ?? connectInit.eligibilityMessage,
                    eligibilityMessage: connectInit.eligibilityMessage,
                    trialRemainingDays: connectInit.trialRemainingDays,
                    currentWorkspaceId: connectInit.currentWorkspaceId,
                    currentWorkspaceName: connectInit.currentWorkspaceName,
                };
            }

            case 'meta-ads':
            case 'facebook-page':
            case 'instagram':
            case 'linkedin-ads':
            case 'linkedinads':
            case 'linkedin':
            case 'pinterest-ads':
            case 'pinterestads':
            case 'pinterest':
            case 'x-ads':
            case 'xads':
            case 'twitter-ads':
            case 'twitterads':
            case 'twitter-x':
            case 'twitterx':
            case 'twitter':
            case 'x':
            case 'youtube-ads':
            case 'youtubeads':
            case 'youtube':
            case 'reddit':
            case 'reddit-social':
            case 'snapchat-ads':
            case 'snapchatads':
            case 'snapchat': {
                const popup = openAuthorizationPopup('Unable to open the authorization window. Please allow pop-ups for Bustly and try again.');

                let connectInit;
                try {
                    connectInit = await authService.initZernioConnect({
                        platform: normalizedProvider,
                        returnUrl: window.location.href,
                        axiosRequestConfig
                    });
                } catch (error) {
                    try { popup.close(); } catch {}
                    throw error;
                }

                if (connectInit.completed) {
                    try { popup.close(); } catch {}
                    await checkZernioIntegrationCreated(normalizedProvider, connectInit.flowId, axiosRequestConfig, {
                        maxAttempts: 10,
                        verification: buildConnectVerificationOptions(normalizedProvider),
                    });
                    return { success: true, flowId: connectInit.flowId };
                }

                if (!connectInit.authUrl) {
                    try { popup.close(); } catch {}
                    throw new Error('We could not start the authorization flow. Please try again.');
                }

                const popupResult = waitForZernioOAuthPopupResult(popup);
                popup.location.replace(connectInit.authUrl);
                popup.focus();

                let stopZernioStatusPolling = false;
                const statusPromise = checkZernioIntegrationCreated(normalizedProvider, connectInit.flowId, axiosRequestConfig, {
                    maxAttempts: 300,
                    shouldStop: () => stopZernioStatusPolling,
                    verification: buildConnectVerificationOptions(normalizedProvider),
                });
                const popupSignalPromise = popupResult
                    .then(() => 'popup-success' as const)
                    .catch((error) => {
                        const message = getErrorMessage(error) || '';
                        if (/window was closed|authorization was cancelled/i.test(message)) {
                            return 'popup-closed' as const;
                        }
                        throw error;
                    });

                try {
                    const winner = await Promise.race([
                        statusPromise.then(() => 'status' as const),
                        popupSignalPromise,
                    ]);

                    if (winner === 'status') {
                        return { success: true, flowId: connectInit.flowId };
                    }

                    if (winner === 'popup-success') {
                        await statusPromise;
                        return { success: true, flowId: connectInit.flowId };
                    }

                    const recoveredAfterPopupClose = await Promise.race([
                        statusPromise.then(() => true),
                        new Promise<boolean>(resolve => window.setTimeout(() => resolve(false), 45000)),
                    ]);

                    if (recoveredAfterPopupClose) {
                        return { success: true, flowId: connectInit.flowId, recoveredAfterPopupClose: true };
                    }

                    stopZernioStatusPolling = true;
                    void statusPromise.catch(() => undefined);
                    throw new Error('Authorization window was closed before the connection finished.');
                } catch (error) {
                    stopZernioStatusPolling = true;
                    void statusPromise.catch(() => undefined);
                    throw error;
                }
            }

            case 'insider-one':
            case 'insider-one-oauth': {
                return connectNangoOAuthDirectly({
                    integrationId: 'insider-one-oauth',
                    providerConfigKey: 'insider-one-oauth',
                    verificationProvider: 'insider-one-oauth',
                    displayName: 'Insider One',
                });
            }

            case 'emarsys':
            case 'emarsys-oauth': {
                return connectNangoOAuthDirectly({
                    integrationId: 'emarsys-oauth',
                    providerConfigKey: 'emarsys-oauth',
                    verificationProvider: 'emarsys-oauth',
                    displayName: 'Emarsys',
                });
            }

            case 'similarweb':
            case 'ahrefs':
            case 'omnisend':
            case 'okendo':
            case 'goaffpro':
            case 'go-aff-pro':
            case 'uppromote':
            case 'up-promote':
            case 'loox':
            case 'loox-reviews':
            case 'trustwill':
            case 'trustwill-loyalty':
            case 'loloyal':
            case 'lo-loyal':
            case 'noxinfluencer':
            case 'nox-influencer':
            case 'nox':
            case 'prestashop':
            case 'presta-shop':
            case 'figma':
            case 'attribuly':
            case 'attribuly-attribution':
            case 'insider-one-api-key': {
                const manualApiKeyProvider = MANUAL_API_KEY_PROVIDER[normalizedProvider] || normalizedProvider;
                const requestBody = {
                    provider: manualApiKeyProvider,
                    apiKey: connectionConfig?.apiKey,
                    accountLabel: connectionConfig?.accountLabel,
                    storeUrl: connectionConfig?.storeUrl,
                    ...buildSceneRequest(connectionConfig),
                };
                if (normalizedProvider === 'prestashop' || normalizedProvider === 'presta-shop') {
                    if (!requestBody.storeUrl || !requestBody.apiKey) {
                        throw new Error('PrestaShop store URL and Webservice key are required');
                    }
                } else if (!requestBody.apiKey) {
                    throw new Error('API key is required');
                }
                const result = await authService.connectManualApiKeyIntegration(requestBody, axiosRequestConfig);
                await fetchIntegrations(false, { forceRefresh: true });
                return result;
            }

                default:
                    throw new Error(`Unsupported platform: ${platform}`);
            }
        } catch (error) {
            const restrictionResult = buildConnectRestrictionResultFromError(error);
            if (restrictionResult) {
                return restrictionResult;
            }
            throw error;
        }
    }, [
        workspaceId,
        allIntegrations,
        checkIntegrationCreated,
        checkZernioIntegrationCreated,
        buildWorkspaceAxiosRequestConfig,
        waitForGoogleAdsConnectionCreatedAfter,
        waitForWorkspaceIntegrationCreatedAfter,
        buildSceneRequest,
        resolveConnectionScene,
        fetchIntegrations,
        resolveConnectionLabel,
        updateConnectedIntegrationLabel,
    ]);

    /**
     * 断开平台集成
     * @param platform - 平台名称（如 'shopify', 'google-ads', 'slack'）
     */
    const disconnect = useCallback(async (platform: string, options?: DisconnectIntegrationOptions) => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const axiosRequestConfig = buildWorkspaceAxiosRequestConfig();

        // 标准化平台名称：同时处理空格和下划线
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');
        const targetMapping = findProviderConnectionMappingForLabel(allIntegrations, normalizedProvider, {
            connectionId: options?.connectionId,
            shopDomain: options?.shopDomain,
            workspaceIntegrationId: options?.workspaceIntegrationId,
        });
        const connectionBackend = String(
            options?.connectionBackend
            || targetMapping?.connection_backend
            || targetMapping?.connectionBackend
            || ''
        ).toUpperCase();
        if (connectionBackend === 'BUSTLY') {
            const integrationId = targetMapping?.id || options?.workspaceIntegrationId;
            if (integrationId == null) {
                throw new Error('Cannot disconnect Bustly integration: no exact connection found');
            }
            await authService.disconnectBustlyIntegration(integrationId, axiosRequestConfig);
            await fetchIntegrations(false, { forceRefresh: true });
            return;
        }
        if (requiresConnectionScopedDisconnect(normalizedProvider) && !options?.connectionId) {
            throw new Error('Cannot disconnect Google Ads: no exact connection found');
        }
        let removeProviderAfterDisconnect: string | null = null;

        switch (normalizedProvider) {
            case 'shopify': {
                const shopifyIntegration = integrations.shopify;
                const shopDomain = resolveShopifyDisconnectDomain(shopifyIntegration, options);
                if (!shopDomain) {
                    throw new Error('Cannot disconnect Shopify: no exact shop domain found');
                }

                await authService.disconnectShopify({
                    shop_domain: shopDomain,
                    connection_scene: options?.connectionScene,
                    axiosRequestConfig
                });
                removeProviderAfterDisconnect = normalizedProvider;
                break;
            }

            case 'google-ads':
            case 'google-analytics':
            case 'tiktok-ads':
            case 'google-search-console':
            case 'bigcommerce':
            case 'woocommerce':
            case 'magento':
            case 'adobe-commerce':
            case 'klaviyo':
            case 'klaviyo-oauth':
            case 'mailchimp':
            case 'mailchimp-oauth':
            case 'semrush':
            case 'figma-oauth':
            case 'emarsys':
            case 'emarsys-oauth': {
                // Nango 集成的断开
                await authService.unbindNangoIntegration({
                    integration_id: PLATFORM_TO_NANGO_ID[normalizedProvider] || normalizedProvider,
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                break;
            }

            case 'feishu': {
                await authService.disconnectFeishu({
                    workspace_id: workspaceId,
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                removeProviderAfterDisconnect = 'feishu';
                break;
            }

            case 'dingtalk': {
                await authService.disconnectDingTalk({
                    workspace_id: workspaceId,
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                removeProviderAfterDisconnect = 'dingtalk';
                break;
            }

            case 'slack': {
                await authService.disconnectSlack({
                    workspace_id: workspaceId
                });
                break;
            }

            case 'aliexpress': {
                // Aliexpress 断开
                await authService.unbindAliexpress({
                    axiosRequestConfig
                });
                break;
            }

            case 'wix': {
                // Wix 断开
                await authService.disconnectWix({
                    workspace_id: workspaceId,
                    instance_id: options?.connectionId,
                    axiosRequestConfig
                });
                break;
            }

            case 'meta-ads':
            case 'facebook-page':
            case 'instagram':
            case 'linkedin-ads':
            case 'linkedinads':
            case 'linkedin':
            case 'pinterest-ads':
            case 'pinterestads':
            case 'pinterest':
            case 'x-ads':
            case 'xads':
            case 'twitter-ads':
            case 'twitterads':
            case 'twitter-x':
            case 'twitterx':
            case 'twitter':
            case 'x':
            case 'youtube-ads':
            case 'youtubeads':
            case 'youtube':
            case 'reddit':
            case 'reddit-social':
            case 'snapchat-ads':
            case 'snapchatads':
            case 'snapchat': {
                const workspaceIntegrationId = options?.workspaceIntegrationId
                    || (Number.isFinite(Number(options?.connectionId)) ? options?.connectionId : undefined);
                await authService.disconnectZernioIntegration({
                    platform: normalizedProvider,
                    workspaceIntegrationId: workspaceIntegrationId == null ? undefined : Number(workspaceIntegrationId),
                    axiosRequestConfig
                });
                break;
            }

            case 'insider-one':
            case 'insider-one-oauth':
            case 'insider-one-api-key': {
                const isManualApiKeyConnection = String(options?.authType || '').toLowerCase() === 'api_key'
                    || options?.native === true
                    || normalizedProvider === 'insider-one-api-key';
                if (isManualApiKeyConnection) {
                    await authService.disconnectManualApiKeyIntegration('insider-one', axiosRequestConfig, options?.workspaceIntegrationId);
                    break;
                }

                await authService.unbindNangoIntegration({
                    integration_id: 'insider-one-oauth',
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                break;
            }

            case 'similarweb':
            case 'ahrefs':
            case 'omnisend':
            case 'okendo':
            case 'goaffpro':
            case 'go-aff-pro':
            case 'uppromote':
            case 'up-promote':
            case 'loox':
            case 'loox-reviews':
            case 'trustwill':
            case 'trustwill-loyalty':
            case 'loloyal':
            case 'lo-loyal':
            case 'noxinfluencer':
            case 'nox-influencer':
            case 'nox':
            case 'prestashop':
            case 'presta-shop':
            case 'attribuly':
            case 'attribuly-attribution': {
                const manualApiKeyProvider = MANUAL_API_KEY_PROVIDER[normalizedProvider] || normalizedProvider;
                await authService.disconnectManualApiKeyIntegration(manualApiKeyProvider, axiosRequestConfig, options?.workspaceIntegrationId);
                break;
            }

            case 'figma': {
                if (resolveFigmaDisconnectTransport(options) === 'manual-api-key') {
                    await authService.disconnectManualApiKeyIntegration(
                        'figma',
                        axiosRequestConfig,
                        options?.workspaceIntegrationId
                    );
                    break;
                }

                await authService.unbindNangoIntegration({
                    integration_id: 'figma',
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                break;
            }

            case 'judgeme':
            case 'judge.me':
            case 'judge-me': {
                await authService.disconnectJudgeMeOAuth({
                    workspace_id: workspaceId,
                    platform: 'judgeme',
                    connection_id: options?.connectionId,
                    integration_id: options?.workspaceIntegrationId,
                });
                break;
            }

            default:
                throw new Error(`Unsupported platform: ${platform}`);
        }

        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        if (removeProviderAfterDisconnect && !isProviderConnected(refreshedIntegrations, removeProviderAfterDisconnect)) {
            removeIntegration(removeProviderAfterDisconnect);
        }
    }, [workspaceId, integrations, allIntegrations, buildWorkspaceAxiosRequestConfig, fetchIntegrations, removeIntegration]);

    useEffect(() => {
        if (!workspaceId || typeof window === 'undefined') return;
        const result = parseJudgeMeOAuthResult(window.location.href);
        if (!result) return;

        const url = new URL(window.location.href);
        url.searchParams.delete('judgeme_oauth');
            url.searchParams.delete('judgeme_platform');
            url.searchParams.delete('judgeme_connection');
            url.searchParams.delete('judgeme_account');
            url.searchParams.delete('judgeme_error');
            url.searchParams.delete('judgeme_flow');
            window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);

        if (result.connected) {
            void fetchIntegrations(false, { forceRefresh: true });
        } else if (result.error) {
            setError(new Error(result.error));
        }
    }, [workspaceId, fetchIntegrations]);

    return {
        integrations,
        allIntegrations,
        isLoading,
        hasFetchedCurrentWorkspace,
        error,
        refresh: fetchIntegrations,
        isConnected,
        getIntegration,
        connect,
        disconnect,
        hasConnectedIntegrations,
    };
};

/**
 * 工具函数：将 UI 中的平台名称转换为 provider 格式
 */
export const normalizePlatformName = (name: string): string => {
    return name.toLowerCase().replace(/\s+/g, '-');
};
